import {
  enginePricingEvidence,
  rememberEnginePricingEvidence,
  type EnginePricingEvidence,
} from '../payroll/engine-pricing-evidence';
/* eslint-disable statura/no-floating-point-money --
 * Canonical Aged Care Award MA000018 regulatory calculator validated
 * against FWC pay guide. Every $-arithmetic line is part of the
 * regulated calc.
 */

/**
 * Phase 5 §7 — Aged Care Award 2010 (MA000018) engine.
 *
 * Implements the per-shift pay calculator for Aged Care employees
 * (Personal Care Workers, AINs not under nursing scope, hospitality,
 * gardening etc.) under MA000018.
 *
 * Reads all rate / penalty / allowance values from the RateContext
 * (Phase 1 effective-dated rate version).
 *
 * KEY ARCHITECTURAL DIFFERENCES from SCHADS / Nurses:
 *
 * 1. **Age-based rate progression** (cl 14 + cl 16 apprentices): MA000018
 *    has no standalone "junior rates" clause; what the engine implements
 *    is a generalised age-banded rate-lookup mechanism that is vestigial
 *    for adult aged-care employees (no age bands on adult cl 14.1 rows).
 *    Apprentice rates at cl 16.4 (cooking) + cl 16.5 (gardening) are
 *    separate. Worker's age-at-shift-date determines the applicable rate
 *    where age_band_min/max columns on award_rate_lines are populated.
 *
 * 2. **Casual loading is INCLUSIVE** (cl 10.4(b)) — penalty multipliers
 *    in the rate version already include the 25% casual loading. This
 *    matches SCHADS convention; differs from Nurses MA000034 (which
 *    Phase 1J reframed as PER-CLAUSE: cl 11.4 additive for cl 20
 *    shiftwork only; multiplicative for cl 19/21/28).
 *
 * 3. **Direct Care work-value tranches** (PR778646 / PR791119):
 *    The 1 Oct 2025 tranche adds MA000018 successor rate-version rows.
 *    The 1 Aug 2026 tranche is PR812118 for MA000034 `aged_care_nurse`
 *    only; it does not apply to this engine.
 *    (Phase 1K citation correction: PR791119 = Aged Care; PR791118 was
 *    the Nurses-side determination — engine had them swapped.)
 *
 * 4. **Stream codes**:
 *    - `aged_care_employee` — cl 14.1 general aged-care employees
 *    - `direct_care` — cl 14.3 direct-care stream (post-PR791119; engine
 *      previously cited cl 14.2 which is cooking apprentices)
 *    - `support_services` — hospitality, laundry, gardening
 *
 * Known v1 limitations (same pattern as Nurses):
 *  - Per-segment afternoon/night shift detection (whole-shift refactor
 *    is a Phase 6 cleanup task).
 *  - OT-end-of-shift assumption for substitution-rule day-type.
 */

import {
  coverageGapsField,
  EBA_REQUIRED_PENALTY_COVERAGE_GAP_REASON,
  pushRateRowCoverageGap,
  resolveRequiredAllowance,
  findWeeklyMinimumAllowance,
  weeklyMinimumScopeKey,
  resolveRequiredPenalty,
  selectRateLineByEmployment,
  type RateContext,
  type RateLine,
  type PenaltyLine,
  type AllowanceLine,
} from '../award-rate-lookup';
import { backOutRoundingUnits, roundMoney, sumMoney } from '../money-rounding';
import type {
  ShiftPayBreakdown,
  ShiftPayInput,
  ShiftPayAllowanceLine,
  ShiftClassificationSnapshot,
  NormalizedSchadsLevel,
} from '../schads';
import type { ShiftPayWithOvertimeInput } from '../payroll/daily-overtime-input';
import { payrollBreakMinutesForEngine } from '../payroll/break-treatment';
import { applyDstStartOrdinaryTopUp } from '../payroll/multi-zone-pay-clock';
import {
  overtimeMealAllowanceCrossings,
  shiftClassificationGeometry,
} from '../payroll/shift-classification-geometry';
import {
  ordinaryHoursForShiftWithinConfiguredLimits,
  subtractOvertimeFromOrdinaryBands,
  ordinaryOvertimeBackoutHours,
} from '../schads';
import {
  classifyShift,
  classifyAgedCareCommencementBand,
  AGED_CARE_COMMENCEMENT_BAND_LOADINGS,
  getOvertimeSegmentBreakdown,
  type AgedCareCommencementBand,
  type ClassifiedShift,
  type DayType,
} from './shift-classifier';
import { calculateRecallPay, type RecallPayBreakdown, type RecallPayInput } from './recall';
import type { AwardEngine, AwardCode } from './types';
import {
  buildNativeEvaluationTrace,
  unavailableEvaluationTrace,
  type EvaluationTrace,
  type TracePrice,
} from './evaluation-trace';
import {
  AGED_CARE_SHIFT_RULES,
  AGED_CARE_RECALL_RULES,
  AGED_CARE_STREAMS,
  AGED_CARE_NAUSEOUS_WORK_EMISSION,
  AGED_CARE_MEAL_OT_EMISSION,
  AGED_CARE_LEADING_HAND_EMISSION,
  AGED_CARE_LEAVE,
  resolveLeadingHandTier,
} from '../award-data/aged-care';
import {
  conditionalPenaltyBlockCodeForKind,
  findConditionalPenaltyLine,
  localTimeHHMM,
  pushMissingConditionalPenaltyGap,
  resolveCategoryScopedShiftLoading,
  resolveShiftLoading,
  hasGeneralStaffDailyLoadedOvertime,
  type ResolvedShiftLoading,
} from '../payroll/eba-conditional-penalties';
import { splitOvernightShiftDayTypeBands } from '../payroll/eba-block-compiler';
import {
  calculateEbaTieredOvertimePay,
  otExportTierHours,
  overtimeOnLoadedRateEnabledFromContext,
  primaryOvertimeRuleFromContext,
  resolveShiftLoadingPremiumFromContext,
} from '../payroll/eba-cert-reducer';
import { dateOnlyInTz } from '../timezone';
import { medeaFortnightCapOvertimeSplit } from '../payroll/award-part-time-overtime';

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

const round2 = (n: number): number => roundMoney(n);
const round4 = (n: number): number => Math.round(n * 10000) / 10000;
const HOURS_EPSILON = 0.000001;

/**
 * Phase 1A.4 + 1B: all engine-local segmentation / day-of-week helpers
 * have been removed; the engine consumes `classifyShift` +
 * `getOvertimeSegmentBreakdown` from `./shift-classifier.ts` for both
 * the base calculation and the OT-end-of-shift refinement.
 */

/** Compute age in whole years at a given date. */
function ageAt(dateOfBirth: string, asOfDate: string): number {
  const dob = new Date(dateOfBirth + 'T00:00:00Z');
  const ref = new Date(asOfDate + 'T00:00:00Z');
  let age = ref.getUTCFullYear() - dob.getUTCFullYear();
  const monthDelta = ref.getUTCMonth() - dob.getUTCMonth();
  if (monthDelta < 0 || (monthDelta === 0 && ref.getUTCDate() < dob.getUTCDate())) {
    age--;
  }
  return age;
}

// ═══════════════════════════════════════════════════════════════════════════
// Classification + rate resolution
// ═══════════════════════════════════════════════════════════════════════════

interface ResolvedRate {
  hourlyRate: number;
  level: string;
  payPoint: string | null;
  ageBandApplied: { min: number | null; max: number | null } | null;
  recognised: boolean;
  includesCasualLoading?: boolean;
}

/** P27 M7 — skip compounding when the catalogue hourly rate is already loaded. */
function agedCareOrdinaryCasualFactor(
  resolved: Pick<ResolvedRate, 'includesCasualLoading'>,
  employment: EmploymentLabel,
): number {
  if (employment !== 'CASUAL') return 1.0;
  if (resolved.includesCasualLoading === true) return 1.0;
  return 1.25;
}

/** GS EA cl 42(b) — the 25% casual loading, applied as a substitutional multiplier. */
const AGED_CARE_CASUAL_LOADING_RATE = 0.25;

/**
 * GS EA cl 42(b)(i)–(iii) — Medea General Staff casual weekend ORDINARY
 * hours (Saturday/Sunday) are priced at 175%/225% of the UNLOADED ordinary
 * hourly rate, "in substitution for and not cumulative upon the casual
 * loading". The Saturday/Sunday penalty multiplier resolved from `ctx`
 * (`satMult`/`sunMult`) already assumes it is being multiplied by an
 * UNLOADED base — it is either the additive `permanent + 0.25` derivation
 * from `eba-block-compiler.ts` or an explicitly declared casual total. If
 * the catalogue rate row is itself stored pre-loaded (`includesCasualLoading`,
 * the Marni Kaulima / P45 convention), the loading must be divided back OUT
 * of the base before that multiplier is applied — unlike
 * `agedCareOrdinaryCasualFactor` (weekday), simply neutralising to 1.0 would
 * leave the loading embedded in the base while the 1.75/2.25 multiplier
 * ALSO re-adds it, over-paying by the same 25% cl 42(b)(iii) removes.
 * Handles both the pre-loaded and not-pre-loaded rate-row conventions
 * consistently: never double-applies, never double-removes. Non-casual and
 * non-flagged rows are byte-identical (returns `baseHourlyRate` unchanged).
 *
 * Scope (engine expectation §3 of the artifact below): the clause is
 * headed "ROSTERED EMPLOYEES" and its cl 26(f)(i) cohort test is not
 * evidenced by any input this engine receives (F3, Path γ — a live-data
 * question, not a source-review one). The artifact's own §"If the worker
 * is not a Rostered Employee" analysis shows base × 1.75/2.25 is the SAFE
 * FLOOR for either cohort (the non-rostered path is never lower under
 * either the additive or multiplicative reading), so this is applied
 * uniformly to EA casual weekend ordinary hours rather than refused —
 * refusing would only re-create the under-pay this fix exists to stop,
 * with no coverage-gap mechanism able to recover the cohort evidence.
 *
 * docs/operations/clause-verifications/2026-09-17-aged-care-legislation-medea-gs-casual-weekend-penalty-substitution-fix-shape.md
 */
function agedCareCasualWeekendUnloadedBase(
  resolved: Pick<ResolvedRate, 'includesCasualLoading'>,
  employment: EmploymentLabel,
  baseHourlyRate: number,
): number {
  if (employment !== 'CASUAL') return baseHourlyRate;
  if (resolved.includesCasualLoading === true) {
    return round4(baseHourlyRate / (1 + AGED_CARE_CASUAL_LOADING_RATE));
  }
  return baseHourlyRate;
}

function lookupRate(
  ctx: RateContext,
  stream: string,
  level: string,
  payPoint: string | null,
  age: number | null,
  employment: EmploymentLabel,
  coverageGaps?: NonNullable<ShiftPayBreakdown['coverageGaps']>,
): RateLine | null {
  const candidates = ctx.rates.filter(
    (r) =>
      r.classificationStream === stream &&
      r.classificationLevel === level &&
      (r.payPoint ?? null) === payPoint,
  );
  if (candidates.length === 0) return null;

  const applyEmploymentSelection = (rows: RateLine[]): RateLine | null => {
    const { rate, ambiguous } = selectRateLineByEmployment(rows, employment);
    if (ambiguous && coverageGaps) {
      pushRateRowCoverageGap(
        coverageGaps,
        stream as RateLine['classificationStream'],
        level,
        payPoint,
        employment,
      );
      return null;
    }
    return rate;
  };

  // No age-banded entries → employment-specific selection (adult / age-agnostic)
  const banded = candidates.filter(
    (r) => (r.ageBandMin ?? null) !== null || (r.ageBandMax ?? null) !== null,
  );
  if (banded.length === 0) return applyEmploymentSelection(candidates);

  // Age-banded — pick the band the worker's age falls within, then employment.
  if (age == null) {
    const adultCandidates = candidates.filter(
      (r) => (r.ageBandMin ?? -Infinity) >= 21 && r.ageBandMax == null,
    );
    const adultPool = adultCandidates.length > 0 ? adultCandidates : candidates;
    return applyEmploymentSelection(adultPool);
  }
  const ageMatches = candidates.filter((r) => {
    const min = r.ageBandMin ?? 0;
    const max = r.ageBandMax ?? Infinity;
    return age >= min && age <= max;
  });
  if (ageMatches.length > 0) return applyEmploymentSelection(ageMatches);

  // Out-of-band — fall back to the highest band the worker exceeds
  const sortedByMin = [...banded].sort((a, b) => (b.ageBandMin ?? 0) - (a.ageBandMin ?? 0));
  const highestCandidates = sortedByMin.filter((r) => age >= (r.ageBandMin ?? 0));
  const pool = highestCandidates.length > 0 ? highestCandidates : candidates;
  return applyEmploymentSelection(pool);
}

function zeroDayTypeHours(): Record<DayType, number> {
  return {
    weekday: 0,
    saturday: 0,
    sunday: 0,
    publicHoliday: 0,
  };
}

function dayTypeHoursFromOrderedBreakTreatment(
  classified: ClassifiedShift,
  breakTreatment: ShiftPayInput['breakTreatment'],
): Record<DayType, number> | null {
  if (!breakTreatment || breakTreatment.status === 'BLOCKED') return null;
  if (classified.segments.length === 0 || breakTreatment.segments.length === 0) return null;

  const result = zeroDayTypeHours();
  let classifierIndex = 0;
  let classifierRemaining = classified.segments[0]!.hours;

  for (const breakSegment of breakTreatment.segments) {
    let remaining = Math.max(0, breakSegment.minutes / 60);
    while (remaining > HOURS_EPSILON && classifierIndex < classified.segments.length) {
      const classifiedSegment = classified.segments[classifierIndex]!;
      const take = Math.min(remaining, classifierRemaining);
      if (breakSegment.countsAsTimeWorked) {
        result[classifiedSegment.dayType] += take;
      }
      remaining -= take;
      classifierRemaining -= take;
      if (classifierRemaining <= HOURS_EPSILON) {
        classifierIndex += 1;
        classifierRemaining =
          classifierIndex < classified.segments.length
            ? classified.segments[classifierIndex]!.hours
            : 0;
      }
    }
  }

  return result;
}

/**
 * Break records count whole minutes, while paid hours (and the overtime taken
 * from them) come from the clock span. When the two differ only by
 * sub-minute clock seconds, scale the day-type split onto the paid total so
 * ordinary + overtime equals the hours worked and recompute/snapshot match
 * preflight. Larger differences are real break facts and are left alone.
 */
function alignDayTypeHoursToPaidHours(
  dayTypeHours: Record<DayType, number>,
  paidHours: number,
): Record<DayType, number> {
  const total = Object.values(dayTypeHours).reduce((sum, hours) => sum + hours, 0);
  const drift = paidHours - total;
  if (total <= HOURS_EPSILON || Math.abs(drift) <= HOURS_EPSILON || Math.abs(drift) >= 1 / 60) {
    return dayTypeHours;
  }
  const scale = paidHours / total;
  const result = zeroDayTypeHours();
  for (const dayType of Object.keys(dayTypeHours) as DayType[]) {
    result[dayType] = dayTypeHours[dayType] * scale;
  }
  return result;
}

function calendarDayType(date: Date, tz: string, isPublicHoliday: boolean): DayType {
  if (isPublicHoliday) return 'publicHoliday';
  const shortDay = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(
    date,
  );
  if (shortDay === 'Sat') return 'saturday';
  if (shortDay === 'Sun') return 'sunday';
  return 'weekday';
}

function ebaPaidDayTypeHours(
  classified: ClassifiedShift,
  rawInput: ShiftPayInput & AgedCareInputExtras,
  hoursWorked: number,
  tz: string,
  isPublicHoliday: boolean,
): Record<DayType, number> {
  if (rawInput.publicHolidaySegments) {
    // Character uses the original envelope; EA money uses this worked fragment only.
    const worked = classifyShift({
      startTime: rawInput.startTime,
      endTime: rawInput.endTime,
      breakMinutes: payrollBreakMinutesForEngine(
        rawInput.breakMinutes ?? 0,
        rawInput.breakTreatment,
        rawInput.breakSemantics,
      ),
      timezone: tz,
      isPublicHoliday,
      publicHolidaySegments: rawInput.publicHolidaySegments,
      shiftCharacterRules: AGED_CARE_SHIFT_RULES,
      dominantDayRule: 'PER_SEGMENT',
    });
    const fromBreak = dayTypeHoursFromOrderedBreakTreatment(worked, rawInput.breakTreatment);
    return fromBreak ? alignDayTypeHoursToPaidHours(fromBreak, hoursWorked) : worked.hoursByDayType;
  }

  const fromBreak = dayTypeHoursFromOrderedBreakTreatment(classified, rawInput.breakTreatment);
  if (fromBreak) return fromBreak;
  const shiftStartTime = rawInput.shiftStartTime ?? localTimeHHMM(rawInput.startTime, tz);
  const shiftEndTime = rawInput.shiftEndTime ?? localTimeHHMM(rawInput.endTime, tz);
  const startDayType = calendarDayType(rawInput.startTime, tz, isPublicHoliday);
  const endDayType = calendarDayType(rawInput.endTime, tz, isPublicHoliday);
  const bands = splitOvernightShiftDayTypeBands({
    shiftStartTime,
    shiftEndTime,
    totalHours: hoursWorked,
    startDayType,
    endDayType,
  });
  if (bands.length === 0) return classified.hoursByDayType;

  const result = zeroDayTypeHours();
  for (const band of bands) {
    result[band.dayType] = round4(result[band.dayType] + band.hours);
  }
  return result;
}

function resolveOrdinaryHoursForOvertime(
  rawInput: ShiftPayWithOvertimeInput,
  totalShiftHours: number,
  defaultDailyLimit: number,
  defaultWeeklyLimit = 38,
): number {
  if (rawInput.ordinaryHoursCapWindows && rawInput.ordinaryHoursCapWindows.length > 0) {
    return medeaFortnightCapOvertimeSplit({
      hoursWorked: totalShiftHours,
      capWindows: rawInput.ordinaryHoursCapWindows,
      dailyOrdinaryCap: rawInput.ordinaryHoursLimitPerShift ?? defaultDailyLimit,
    }).ordinaryHours;
  }
  return ordinaryHoursForShiftWithinConfiguredLimits(
    rawInput,
    totalShiftHours,
    defaultDailyLimit,
    defaultWeeklyLimit,
  );
}

export type AgedCarePublicHolidayElection = 'CASH_150' | 'ANNUAL_LEAVE_HOURS';

/** Engine evidence for an applied cl 29.2(a) election (read by the leave ledger). */
export interface AgedCarePublicHolidayElectionOutcome {
  election: AgedCarePublicHolidayElection;
  /** Public-holiday ordinary hours to credit to annual leave. */
  leaveCreditHours: number;
  /** The additional 150% not paid in cash because the hours go to leave. */
  cashPremiumNotPaid: number;
  clauseRef: string;
}

export type AgedCareShiftPayBreakdown = ShiftPayBreakdown & {
  publicHolidayElection?: AgedCarePublicHolidayElectionOutcome;
};

export const AGED_CARE_PUBLIC_HOLIDAY_ELECTION_CLAUSE = 'MA000018 cl 29.2(a)(ii)';
export const AGED_CARE_PUBLIC_HOLIDAY_ELECTION_CLAUSE_PART_TIME = 'MA000018 cl 29.2(b)(ii)';

interface AgedCareInputExtras {
  classificationStream?: string;
  classificationLevel?: string;
  classificationPayPoint?: string | null;
  /** YYYY-MM-DD — used for junior-rate lookup. */
  dateOfBirth?: string;
  /**
   * Phase 1D (Award Engine Integrity 2026-05) — worker's substantive
   * classification. When provided and differs from the engine's resolved
   * (acting) classification, `breakdown.actingClassification` +
   * `substantiveClassification` are both populated. See nurses.ts +
   * shared schads.ts `ShiftClassificationSnapshot` for the shape.
   */
  substantiveClassification?: ShiftClassificationSnapshot;
  /**
   * MA000018 cl 29.2(a) full-time public-holiday election (MZ-004, Paul 6 Oct
   * 2026), resolved by payroll from `staff_public_holiday_elections` for the
   * shift's date. Absent or CASH_150 = today's behaviour (ordinary pay plus an
   * additional 150%). ANNUAL_LEAVE_HOURS = ordinary pay only; the same hours are
   * credited to annual leave through the leave entitlement proposal ledger.
   * Applied on the base award only (not under an enterprise agreement).
   */
  publicHolidayElection?: AgedCarePublicHolidayElection | null;
  /**
   * Phase W4 (Award Engine Integrity 2026-05-17) — hours of nauseous work
   * performed on this shift. When > 0, the engine emits a NAUSEOUS_WORK
   * allowance line at `$0.60 × Math.ceil(nauseousWorkHours)` per
   * MA000018 cl 15.5(a) ("per hour or part thereof"). Defaults to
   * `undefined` (no emission). Coordinator UI / mobile data-capture is W4.1.
   * Weekly minimum $3.24/wk is a cross-shift pay-run aggregator (also W4.1).
   */
  nauseousWorkHours?: number | null;
  /**
   * Phase W4 (Award Engine Integrity 2026-05-17) — if true, the employer
   * provided an adequate meal during OT (cl 15.4(a)(i) provision-of-meal
   * carve-out). Suppresses BOTH MEAL and MEAL_OT_ADDITIONAL emissions.
   * Defaults to `undefined`/`false` (no carve-out → allowances fire when
   * OT thresholds are met). Coordinator UI / mobile data-capture is W4.1.
   */
  mealProvidedDuringOT?: boolean | null;
  /**
   * Phase W4 (Award Engine Integrity 2026-05-17) — rare exceptional
   * carve-out: if true, the worker could feasibly return home for the
   * meal, so the allowance is not payable. Suppresses BOTH MEAL and
   * MEAL_OT_ADDITIONAL emissions. Defaults to `undefined`/`false`.
   */
  couldReturnHomeForMeal?: boolean | null;
  /**
   * Phase W4.0.5 (Award Engine Integrity 2026-05-17) — number of OTHER
   * employees this worker is designated to supervise as leading-hand for
   * this shift (cl 15.3(a)). When `>= 2`, the engine resolves the tier
   * (`AGED_CARE_LEADING_HAND_EMISSION.tiers`), reads the per-week DB
   * allowance amount, and LIFTS `baseHourlyRate` BEFORE penalty/OT/shift-
   * loading multipliers per cl 15.3(c) "part of salary for all purposes"
   * all-purpose mechanics. Defaults to `undefined`/`null` (not in charge).
   *
   * Worker counts OTHER workers, not themselves: 1 LH + 5 colleagues = 5.
   * Sub-minimum (`null`/`0`/`1`) → no lift. Per-shift binary (whole-shift
   * designation; per-hour granular partial-shift handover rejected per
   * W4.0.5 specialist verification 2026-05-17).
   *
   * The engine TRUSTS the caller's count without validating classification
   * eligibility — coordinator UI (W4.1) is responsible for surfacing the
   * cl 15.3(a) "denotes supervisory responsibility" carve-out (e.g.,
   * direct_care L6 Team Leader presumptively excluded).
   */
  leadingHandSupervisedCount?: number | null;
  /** Local HH:MM shift end for EBA day-type banding when `endTime` alone is insufficient. */
  shiftEndTime?: string | null;
}

/**
 * Phase 1D helper — returns acting/substantive pair for the breakdown.
 * Same shape + semantics as nurses.ts. Inlined here rather than shared to
 * avoid creating yet another `award-engines/` utility module for a 20-line
 * helper; the function body is engine-agnostic so divergence between the
 * three implementations is a clear signal worth review.
 */
function resolveActingVsSubstantive(
  resolvedStream: string | null,
  resolvedLevel: string,
  resolvedPayPoint: string | null,
  substantive: ShiftClassificationSnapshot | undefined,
): {
  actingClassification: ShiftClassificationSnapshot | null;
  substantiveClassification: ShiftClassificationSnapshot | null;
} {
  if (!substantive) {
    return { actingClassification: null, substantiveClassification: null };
  }
  const sameAsActing =
    substantive.classificationStream === resolvedStream &&
    substantive.classificationLevel === resolvedLevel &&
    (substantive.payPoint ?? null) === (resolvedPayPoint ?? null);
  if (sameAsActing) {
    return { actingClassification: null, substantiveClassification: null };
  }
  return {
    actingClassification: {
      classificationStream: resolvedStream,
      classificationLevel: resolvedLevel,
      payPoint: resolvedPayPoint ?? null,
    },
    substantiveClassification: {
      classificationStream: substantive.classificationStream,
      classificationLevel: substantive.classificationLevel,
      payPoint: substantive.payPoint,
    },
  };
}

function resolveAgedCareRate(
  ctx: RateContext,
  input: ShiftPayInput & AgedCareInputExtras,
  shiftDate: string,
  employment: EmploymentLabel,
  coverageGaps?: NonNullable<ShiftPayBreakdown['coverageGaps']>,
): ResolvedRate {
  const stream = input.classificationStream ?? 'aged_care_employee';
  const level = input.classificationLevel;
  const payPoint = input.classificationPayPoint ?? null;
  if (!level) {
    return { hourlyRate: 0, level: '', payPoint, ageBandApplied: null, recognised: false };
  }

  const age = input.dateOfBirth ? ageAt(input.dateOfBirth, shiftDate) : null;
  const rate = lookupRate(ctx, stream, level, payPoint, age, employment, coverageGaps);
  if (!rate) {
    return { hourlyRate: 0, level, payPoint, ageBandApplied: null, recognised: false };
  }
  return {
    hourlyRate: rate.hourlyRate,
    level,
    payPoint,
    ageBandApplied:
      rate.ageBandMin != null || rate.ageBandMax != null
        ? { min: rate.ageBandMin ?? null, max: rate.ageBandMax ?? null }
        : null,
    recognised: true,
    includesCasualLoading: rate.includesCasualLoading,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Penalty + allowance lookups (mirror Nurses pattern; SCHADS-style inclusive casual)
// ═══════════════════════════════════════════════════════════════════════════

type EmploymentLabel = 'PERMANENT' | 'CASUAL';

function findPenalty(
  penalties: PenaltyLine[],
  band: string,
  employment: EmploymentLabel,
): number | null {
  return (
    penalties.find((p) => p.band === band && p.employmentType === employment)?.multiplier ?? null
  );
}

function findAllowance(
  allowances: AllowanceLine[],
  code: string,
  employment: EmploymentLabel | null,
): AllowanceLine | null {
  const employmentKey = employment === 'CASUAL' ? 'CASUAL' : 'PERMANENT';
  const specific = allowances.find(
    (a) => a.allowanceCode === code && a.appliesToEmployment === employmentKey,
  );
  if (specific) return specific;
  return (
    allowances.find((a) => a.allowanceCode === code && (a.appliesToEmployment ?? null) === null) ??
    null
  );
}

function resolveAllowance(
  ctx: RateContext,
  code: string,
  employment: EmploymentLabel | null,
  coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']>,
): AllowanceLine | null {
  return resolveRequiredAllowance(ctx, code, employment, {
    lookup: (allowanceCode, lookupEmployment) =>
      findAllowance(ctx.allowances, allowanceCode, lookupEmployment as EmploymentLabel | null),
    coverageGaps,
  });
}

function conditionalOrdinaryMultiplier(employment: EmploymentLabel): number {
  return employment === 'CASUAL' ? 1.25 : 1.0;
}

function conditionalPenaltyExtraPay(input: {
  baseHourlyRate: number;
  hours: number;
  totalMultiplier: number;
  ordinaryMultiplier: number;
}): number {
  return round2(
    input.baseHourlyRate *
      Math.max(0, input.hours) *
      Math.max(0, input.totalMultiplier - input.ordinaryMultiplier),
  );
}

const ZERO_BREAKDOWN = (
  level: string | null,
  baseHourlyRate: number,
  warnings: string[],
): ShiftPayBreakdown => ({
  normalizedLevel: level as NormalizedSchadsLevel | null,
  baseHourlyRate,
  hoursWorked: 0,
  bands: {
    weekdayOrdinary: 0,
    saturdayOrdinary: 0,
    sundayOrdinary: 0,
    publicHoliday: 0,
    eveningShift: 0,
    nightShift: 0,
  },
  basePay: 0,
  penaltyPay: 0,
  shiftLoadingPay: 0,
  overtimeHours: 0,
  overtimePay: 0,
  overtimeFirstHours: 0,
  overtimeRemainderHours: 0,
  overtimePublicHolidayHours: 0,
  allowancesPay: 0,
  allowanceLines: [],
  grossTotal: 0,
  warnings,
});

// ═══════════════════════════════════════════════════════════════════════════
// Engine
// ═══════════════════════════════════════════════════════════════════════════

type DailyPairShiftLoading = ResolvedShiftLoading & {
  weekdayOnly: boolean;
  ordinaryLoading: ResolvedShiftLoading;
};

function dailyPairShiftLoading(
  rawInput: ShiftPayInput & AgedCareInputExtras,
  ctx: RateContext,
): DailyPairShiftLoading {
  const geometry = shiftClassificationGeometry(rawInput);
  const tz = rawInput.timezone ?? 'Australia/Sydney';
  const classified = classifyShift({
    ...geometry,
    timezone: tz,
    isPublicHoliday: rawInput.isPublicHoliday ?? false,
    publicHolidaySegments: rawInput.publicHolidaySegments,
    shiftCharacterRules: AGED_CARE_SHIFT_RULES,
    dominantDayRule: 'STRICT_MAJORITY',
    permanentNightShiftWorker: false,
  });
  const ordinaryLoading = resolveShiftLoading(
    ctx,
    rawInput.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT',
    {
      employeeCategory: rawInput.employeeCategory ?? null,
      shiftStartTime: localTimeHHMM(geometry.startTime, tz),
      shiftEndTime: localTimeHHMM(geometry.endTime, tz),
      // Resolve character from the envelope; paid weekday minutes below decide
      // whether this loading is payable. Weekend/holiday rates substitute only
      // for their own minutes, regardless of which day owns the majority.
    },
  );
  return {
    // Preserve the existing loaded-OT decision; F5 changes ordinary minutes only.
    ...(classified.dominantDayType === 'weekday'
      ? ordinaryLoading
      : { line: null, premium: 0, unclassified: false }),
    ordinaryLoading,
    weekdayOnly:
      classified.hoursByDayType.saturday === 0 &&
      classified.hoursByDayType.sunday === 0 &&
      classified.hoursByDayType.publicHoliday === 0,
  };
}

/**
 * Exact (unrounded) ordinary pay units that calculate() rounded into basePay
 * (weekday) and penaltyPay (Saturday, Sunday, public holiday), keyed by the
 * breakdown it returned. Never serialised. calculateWithOvertime backs out
 * overtime per unit from these (Astra RND-04/RND-07), so any other money in
 * the same component (e.g. a missed-meal penalty) is carried through exactly.
 */
const ORDINARY_PAY_UNITS = new WeakMap<
  ShiftPayBreakdown,
  { weekday: number; saturday: number; sunday: number; publicHoliday: number }
>();

function calculate(
  rawInput: ShiftPayInput & AgedCareInputExtras,
  ctx?: RateContext,
  dailyLoading?: ResolvedShiftLoading,
): ShiftPayBreakdown {
  const warnings: string[] = [];
  if (!ctx) {
    warnings.push(
      'AgedCareEngine called without a RateContext — Phase 1 always loads ctx from DB; this path is for dev tests only.',
    );
    return ZERO_BREAKDOWN(null, 0, warnings);
  }

  const stream = rawInput.classificationStream ?? 'aged_care_employee';
  if (!AGED_CARE_STREAMS.has(stream)) {
    warnings.push(
      `AgedCareEngine called with classification stream '${stream}' — only ${Array.from(AGED_CARE_STREAMS).join(', ')} are supported.`,
    );
    return ZERO_BREAKDOWN(null, 0, warnings);
  }

  // Resolve age-aware rate
  const tz = rawInput.timezone ?? 'Australia/Sydney';
  const shiftDate = dateOnlyInTz(new Date(rawInput.startTime), tz);
  const employment: EmploymentLabel = rawInput.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
  const coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']> = [];
  const resolved = resolveAgedCareRate(ctx, rawInput, shiftDate, employment, coverageGaps);
  if (!resolved.recognised) {
    warnings.push(
      `Aged Care classification "${rawInput.classificationLevel ?? '?'}" not recognised under stream '${stream}' — gross pay will be $0.`,
    );
  }
  const employeeCategory = rawInput.employeeCategory ?? 'dayworker';
  const isShiftworker = employeeCategory === 'shiftworker';

  // Phase W4.0.5 (Award Engine Integrity 2026-05-17) — cl 15.3 LEADING_HAND
  // all-purpose base-rate lift. Per cl 15.3(c) "part of salary for all
  // purposes" + Pay Guide page 24 asterisk + FWO K600467, the per-hour
  // amount LIFTS baseHourlyRate BEFORE every penalty/OT/shift-loading
  // multiplier. The lifted rate propagates through all downstream band
  // calculations automatically. Engine TRUSTS the caller's
  // leadingHandSupervisedCount input without validating classification
  // eligibility (cl 15.3(a) "denotes supervisory responsibility" carve-out
  // is W4.1 coordinator-UI concern). Per-hour conversion is computed at
  // runtime from the DB weekly amount (NOT a baked rounded constant) to
  // avoid 0.3% accumulated drift across multi-shift pay runs.
  let leadingHandLiftPerHour = 0;
  let leadingHandAllPurposeLift: ShiftPayBreakdown['leadingHandAllPurposeLift'] = null;
  if (resolved.recognised) {
    const lhTier = resolveLeadingHandTier(rawInput.leadingHandSupervisedCount);
    if (lhTier) {
      const lhAllowance = resolveAllowance(ctx, lhTier.allowanceCode, employment, coverageGaps);
      if (lhAllowance) {
        leadingHandLiftPerHour = lhAllowance.amount / AGED_CARE_LEADING_HAND_EMISSION.weeklyDivisor;
        leadingHandAllPurposeLift = {
          tierAllowanceCode: lhTier.allowanceCode,
          supervisedCount: rawInput.leadingHandSupervisedCount as number,
          weeklyAmount: lhAllowance.amount,
          liftPerHour: leadingHandLiftPerHour,
        };
        warnings.push(
          `Leading hand all-purpose lift applied: tier ${lhTier.allowanceCode}, supervisedCount=${rawInput.leadingHandSupervisedCount}, +$${leadingHandLiftPerHour.toFixed(4)}/hr (cl 15.3 — "part of salary for all purposes").`,
        );
      } else {
        warnings.push(
          `Leading hand requested (supervisedCount=${rawInput.leadingHandSupervisedCount}) but DB allowance row '${lhTier.allowanceCode}' not found — no lift applied.`,
        );
      }
    } else if (
      rawInput.leadingHandSupervisedCount != null &&
      rawInput.leadingHandSupervisedCount > 0 &&
      rawInput.leadingHandSupervisedCount < AGED_CARE_LEADING_HAND_EMISSION.minimumSupervisedCount
    ) {
      warnings.push(
        `Leading hand sub-minimum: cl 15.3(a) requires at least ${AGED_CARE_LEADING_HAND_EMISSION.minimumSupervisedCount} other employees supervised (received ${rawInput.leadingHandSupervisedCount}); no lift applied.`,
      );
    }
  }
  const baseHourlyRate = resolved.hourlyRate + leadingHandLiftPerHour;
  const normalizedLevel = resolved.recognised ? (resolved.level as NormalizedSchadsLevel) : null;

  if (resolved.ageBandApplied) {
    warnings.push(
      `Junior rate applied: age band [${resolved.ageBandApplied.min ?? '-'}–${resolved.ageBandApplied.max ?? '+'}].`,
    );
  }

  const totalMs = rawInput.endTime.getTime() - rawInput.startTime.getTime();
  if (totalMs <= 0) {
    warnings.push('Shift end must be after start — returning zero pay.');
    return ZERO_BREAKDOWN(normalizedLevel, baseHourlyRate, warnings);
  }
  const rawHours = totalMs / (1000 * 60 * 60);
  const deductibleBreakMinutes = payrollBreakMinutesForEngine(
    rawInput.breakMinutes ?? 0,
    rawInput.breakTreatment,
    rawInput.breakSemantics,
  );
  const breakHours = deductibleBreakMinutes / 60;
  const hoursWorked = Math.max(0, rawHours - breakHours);

  if (rawInput.publicHolidayNotWorked) {
    const publicHolidayNotWorkedBlockCode = conditionalPenaltyBlockCodeForKind(
      ctx,
      'PUBLIC_HOLIDAY_NOT_WORKED',
      rawInput.conditionalPenaltyCode ?? null,
      employeeCategory,
    );
    const line = findConditionalPenaltyLine(ctx, 'PUBLIC_HOLIDAY_NOT_WORKED', employment, {
      blockCode: publicHolidayNotWorkedBlockCode,
      employeeCategory,
    });
    if (!line) {
      pushMissingConditionalPenaltyGap(
        coverageGaps,
        ctx,
        'PUBLIC_HOLIDAY_NOT_WORKED',
        employment,
        publicHolidayNotWorkedBlockCode,
        employeeCategory,
      );
    }
    const paidHours = rawInput.usualRosteredHours ?? hoursWorked;
    const publicHolidayNotWorkedPay = line
      ? round2(paidHours * baseHourlyRate * line.multiplier)
      : 0;
    return {
      normalizedLevel,
      baseHourlyRate,
      hoursWorked: round4(paidHours),
      bands: {
        weekdayOrdinary: 0,
        saturdayOrdinary: 0,
        sundayOrdinary: 0,
        publicHoliday: round4(paidHours),
        eveningShift: 0,
        nightShift: 0,
      },
      basePay: publicHolidayNotWorkedPay,
      penaltyPay: 0,
      shiftLoadingPay: 0,
      overtimeHours: 0,
      overtimePay: 0,
      overtimeFirstHours: 0,
      overtimeRemainderHours: 0,
      overtimePublicHolidayHours: 0,
      allowancesPay: 0,
      allowanceLines: [],
      grossTotal: publicHolidayNotWorkedPay,
      warnings,
      ...coverageGapsField(coverageGaps),
      leadingHandAllPurposeLift,
    };
  }

  // Phase 1A.4 (2026-05-16) — whole-shift majority for day-type selection.
  // Phase Codex Pass A (2026-05-19) — `AGED_CARE_SHIFT_RULES` is now
  // EMPTY (no afternoon/night/orTrigger). `classifyShift` returns
  // `shiftCharacter: 'DAY'` for AC; we derive the cl 26.1 commencement
  // band separately via `classifyAgedCareCommencementBand`. The
  // `dominantDayType` selection (used for penalty-band assignment) is
  // unchanged and still routes through `classifyShift`.
  const classificationTimes = shiftClassificationGeometry(rawInput);
  const classified = classifyShift({
    startTime: classificationTimes.startTime,
    endTime: classificationTimes.endTime,
    breakMinutes: classificationTimes.breakMinutes,
    timezone: tz,
    isPublicHoliday: rawInput.isPublicHoliday ?? false,
    publicHolidaySegments: rawInput.publicHolidaySegments,
    shiftCharacterRules: AGED_CARE_SHIFT_RULES,
    dominantDayRule: 'STRICT_MAJORITY',
    permanentNightShiftWorker: false,
  });

  const bands = {
    weekdayOrdinary: 0,
    saturdayOrdinary: 0,
    sundayOrdinary: 0,
    publicHoliday: 0,
    eveningShift: 0,
    nightShift: 0,
  };

  // Phase Codex Pass A (2026-05-19) — cl 26.1 commencement-band
  // classification. The 4 bands map to the existing `eveningShift` /
  // `nightShift` breakdown fields (for `ShiftPayBreakdown.bands` shape
  // compatibility with downstream consumers like `roster-cost-estimate`).
  //
  // Band-to-breakdown-field mapping:
  //   AFTERNOON_10    (10%)   → eveningShift
  //   AFTERNOON_12_5  (12.5%) → eveningShift
  //   NIGHT_15        (15%)   → nightShift
  //   EARLY_MORNING_10(10%)   → nightShift  (shape-historic; band is correct)
  //   NONE                    → no shift loading
  //
  // This preserves the `ShiftPayBreakdown.bands` shape (no new fields).
  // The dollar effect comes from the per-band loading lookup below.
  let commencementBand: AgedCareCommencementBand = 'NONE';
  const shiftStartTime =
    rawInput.shiftStartTime ?? localTimeHHMM(classificationTimes.startTime, tz);
  const shiftEndTime = localTimeHHMM(classificationTimes.endTime, tz);
  // Raw category BEFORE the null→dayworker collapse above — the new
  // category-scoped path must distinguish "unclassified" (fail closed) from a
  // genuine day-worker.
  const rawEmployeeCategory = rawInput.employeeCategory ?? null;

  // A new-style EA rule version scopes its weekday afternoon/night shift-loading
  // lines by employee category (and may set windowMode:'SPAN' for General EA
  // cl 44). When such a line is present we resolve the loading DETERMINISTICALLY
  // from the shift times + category — not from a source `conditionalPenaltyCode`
  // — and FAIL CLOSED on an unknown category. When no such line is present
  // (legacy seeded data), the original code-gated, shiftworker-only commencement
  // path is preserved unchanged (byte-for-byte).
  const ebaCategoryScopedShiftLoading =
    ctx.isEba &&
    (ctx.conditionalPenalties ?? []).some(
      (line) =>
        line.kind === 'SHIFT_START_WINDOW' &&
        ((line.employeeCategories?.length ?? 0) > 0 || line.windowMode != null),
    );

  const ebaShiftStartWindowCodeMatch =
    ctx.isEba && !ebaCategoryScopedShiftLoading && rawInput.conditionalPenaltyCode
      ? findConditionalPenaltyLine(ctx, 'SHIFT_START_WINDOW', employment, {
          blockCode: rawInput.conditionalPenaltyCode,
          employeeCategory,
        })
      : null;
  const legacyEbaShiftLoadingLine = ebaShiftStartWindowCodeMatch
    ? findConditionalPenaltyLine(ctx, 'SHIFT_START_WINDOW', employment, {
        blockCode: rawInput.conditionalPenaltyCode ?? null,
        shiftStartTime,
        employeeCategory,
      })
    : null;

  // Unified resolved shift-loading line (legacy or new). Display split of the
  // loading hours across eveningShift/nightShift is cosmetic; the DOLLAR uses
  // the sum (shiftStartWindowHours) × the line multiplier, and the two-oracle
  // cert compares the shiftLoading money component, not the band split.
  let ebaShiftLoadingLine: ReturnType<typeof findConditionalPenaltyLine> =
    legacyEbaShiftLoadingLine;
  let ebaShiftLoadingUnclassified = false;

  if (ctx?.isEba) {
    if (
      !dailyLoading &&
      classified.dominantDayType !== 'weekday' &&
      hasGeneralStaffDailyLoadedOvertime(ctx)
    ) {
      dailyLoading = dailyPairShiftLoading(rawInput, ctx).ordinaryLoading;
    }
    const dayTypeHours = ebaPaidDayTypeHours(
      classified,
      rawInput,
      hoursWorked,
      tz,
      rawInput.isPublicHoliday ?? false,
    );
    bands.publicHoliday = dayTypeHours.publicHoliday;
    bands.saturdayOrdinary = dayTypeHours.saturday;
    bands.sundayOrdinary = dayTypeHours.sunday;
    bands.weekdayOrdinary = dayTypeHours.weekday;
    if (
      bands.weekdayOrdinary > 0 &&
      (classified.dominantDayType === 'weekday' || ebaCategoryScopedShiftLoading || dailyLoading)
    ) {
      if (dailyLoading) {
        const loading = dailyLoading;
        ebaShiftLoadingUnclassified = loading.unclassified;
        ebaShiftLoadingLine = loading.line;
        if (loading.premium > 0) bands.eveningShift = bands.weekdayOrdinary;
      } else if (ebaCategoryScopedShiftLoading) {
        // Shared resolver → engine and reducer make the SAME fail-closed + line
        // decision. Unclassified (null/empty OR an unrecognised category such
        // as OTHER/typo) → no loading + coverage gap, never a silent day-worker.
        const resolved = resolveCategoryScopedShiftLoading(ctx, employment, {
          employeeCategory: rawEmployeeCategory,
          shiftStartTime: rawInput.shiftEnvelopeStartTime
            ? localTimeHHMM(classificationTimes.startTime, tz)
            : shiftStartTime,
          shiftEndTime,
        });
        if (resolved.unclassified) {
          ebaShiftLoadingUnclassified = true;
        } else if (resolved.line) {
          ebaShiftLoadingLine = resolved.line;
          bands.eveningShift = bands.weekdayOrdinary;
        }
      } else if (isShiftworker && ebaShiftStartWindowCodeMatch) {
        commencementBand = classifyAgedCareCommencementBand(classificationTimes.startTime, tz);
        if (commencementBand === 'AFTERNOON_10' || commencementBand === 'AFTERNOON_12_5') {
          bands.eveningShift = bands.weekdayOrdinary;
        } else if (commencementBand === 'NIGHT_15' || commencementBand === 'EARLY_MORNING_10') {
          bands.nightShift = bands.weekdayOrdinary;
        }
      }
    }
  } else {
    switch (classified.dominantDayType) {
      case 'publicHoliday':
        bands.publicHoliday = hoursWorked;
        break;
      case 'saturday':
        bands.saturdayOrdinary = hoursWorked;
        break;
      case 'sunday':
        bands.sundayOrdinary = hoursWorked;
        break;
      case 'weekday':
        bands.weekdayOrdinary = hoursWorked;
        if (isShiftworker) {
          commencementBand = classifyAgedCareCommencementBand(classificationTimes.startTime, tz);
          if (commencementBand === 'AFTERNOON_10' || commencementBand === 'AFTERNOON_12_5') {
            bands.eveningShift = hoursWorked;
          } else if (commencementBand === 'NIGHT_15' || commencementBand === 'EARLY_MORNING_10') {
            bands.nightShift = hoursWorked;
          }
        }
        break;
    }
  }

  // Aged Care: SCHADS-style inclusive casual penalty rates (cl 10.4(b))
  // — Phase 1K citation correction (was cl 11 = Termination of employment).
  const weekdayMult = agedCareOrdinaryCasualFactor(resolved, employment);
  const penaltyLookup = (band: string, emp: EmploymentLabel) =>
    findPenalty(ctx.penalties, band, emp);
  const satMult = resolveRequiredPenalty(ctx, 'SATURDAY', employment, 1.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const sunMult = resolveRequiredPenalty(ctx, 'SUNDAY', employment, 2.0, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const phMult = resolveRequiredPenalty(ctx, 'PUBLIC_HOLIDAY', employment, 2.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });

  const weekdayOrdinaryExact = bands.weekdayOrdinary * baseHourlyRate * weekdayMult;
  const basePay = round2(weekdayOrdinaryExact);
  // GS EA cl 42(b) — Saturday/Sunday casual ORDINARY hours price off the
  // unloaded base; see agedCareCasualWeekendUnloadedBase() above. Non-casual
  // and non-pre-loaded rows are byte-identical (returns baseHourlyRate).
  const casualWeekendBase = agedCareCasualWeekendUnloadedBase(resolved, employment, baseHourlyRate);
  const saturdayExact = bands.saturdayOrdinary * casualWeekendBase * satMult;
  const sundayExact = bands.sundayOrdinary * casualWeekendBase * sunMult;
  const satPay = round2(saturdayExact);
  const sunPay = round2(sundayExact);
  // Public holiday: cl 42 has no PH limb (artifact F5) — the correct PH
  // casual ordinary rate is cl 31(d)(iii), not cl 42(b), and is UNRESOLVED
  // pending human primary-source review. Left on baseHourlyRate exactly as
  // before; the additive PH casual multiplier this engine already derives
  // (2.5 + 0.25 = 2.75 via eba-block-compiler.ts) happens to coincide with
  // the artifact's own additive fallback reading, so this is not known to
  // under-pay, but it is NOT clause-verified and must not be ported onto
  // 42(b)'s substitution without its own clause-verification artifact.
  // Casual loaded-rate parity only: an already-loaded agreed base is
  // normalized to the unloaded base so the unchanged multiplier is applied once.
  const casualPublicHolidayBase = agedCareCasualWeekendUnloadedBase(
    resolved,
    employment,
    baseHourlyRate,
  );
  const publicHolidayExact = bands.publicHoliday * casualPublicHolidayBase * phMult;
  const phPay = round2(publicHolidayExact);
  let penaltyPay = round2(satPay + sunPay + phPay);

  // Phase Codex Pass A (2026-05-19) — cl 26.1 commencement-band loading.
  // Loading percentage (0.10 / 0.125 / 0.15 / 0.10 / 0.00) is applied
  // ADDITIVELY on top of the weekday-ordinary base rate.
  //
  // ── Casual treatment: TWO PATHS, DELIBERATELY DIFFERENT ──────────────────
  //
  // EBA path (`ebaShiftLoadingLine`, below) — ADDITIVE. The compiled line
  // already carries the casual total (e.g. cl 44 afternoon 1.15 + 0.25 = 1.40)
  // and the engine subtracts the ordinary casual multiplier, yielding the bare
  // loading on the UNLOADED ordinary hourly rate. Authority + verbatim clause
  // text live at `conditionalTotalMultiplier()` in eba-block-compiler.ts and
  // docs/operations/clause-verifications/2026-07-25-medea-ea-cl44-casual-shift-loading-additive.md
  //
  // Base-award MA000018 path (`casualLoadingMult`, the else-branch below) —
  // still COMPOUNDS (loading × base × hours × 1.25), mirroring the DB-seeded
  // inclusive casual multipliers 1.40625 (12.5%) and 1.4375 (15%).
  //
  // ⚠ UNVERIFIED — this compounding is NOT primary-source authorised. It was
  // previously justified in-code by citing MA000018 cl 10.4(b), which merely
  // defines the 25% casual loading and says nothing about multiplying shift
  // loadings; that citation was a misuse and has been withdrawn. The award's
  // own "ordinary hourly rate" definition for shift loadings (cl 26.3) has NOT
  // been read against primary source in this change set, so the base-award
  // treatment is left EXACTLY as it was rather than ported across from the EA
  // reading. Compounding over-pays relative to the additive reading, so the
  // deferral carries no underpayment exposure. Resolving it requires its own
  // clause-verification artifact covering MA000018 cl 26.3 AND a re-seed of the
  // 1.40625 / 1.4375 national multipliers — tracked as the follow-up slice in
  // the artifact above. Do not "align" this line without that evidence.
  const casualLoadingMult = agedCareOrdinaryCasualFactor(resolved, employment);
  if (
    ctx.isEba &&
    rawInput.conditionalPenaltyCode &&
    !ebaShiftLoadingLine &&
    bands.eveningShift + bands.nightShift > 0
  ) {
    pushMissingConditionalPenaltyGap(
      coverageGaps,
      ctx,
      'SHIFT_START_WINDOW',
      employment,
      rawInput.conditionalPenaltyCode,
      employeeCategory,
    );
  }
  // Fail-closed: a category-scoped EA version could not classify this worker
  // (unknown employee_category) — raise a coverage gap so the shift is surfaced
  // for classification rather than silently paid without its loading.
  if (ebaShiftLoadingUnclassified) {
    coverageGaps.push({
      band: 'WEEKDAY_AFTERNOON',
      blockKind: 'PENALTY',
      employment,
      reason: 'EBA_SHIFT_LOADING_EMPLOYEE_CATEGORY_UNCLASSIFIED',
    });
  }
  const commencementBandLoading = AGED_CARE_COMMENCEMENT_BAND_LOADINGS[commencementBand];
  const shiftStartWindowHours = bands.eveningShift + bands.nightShift;
  const shiftLoadingPay = ebaShiftLoadingLine
    ? conditionalPenaltyExtraPay({
        baseHourlyRate: agedCareCasualWeekendUnloadedBase(resolved, employment, baseHourlyRate),
        hours: shiftStartWindowHours,
        totalMultiplier: ebaShiftLoadingLine.multiplier,
        ordinaryMultiplier: conditionalOrdinaryMultiplier(employment),
      })
    : round2(
        (bands.eveningShift + bands.nightShift) *
          baseHourlyRate *
          commencementBandLoading *
          casualLoadingMult,
      );

  if (rawInput.missedMealBreak) {
    const mealBreakBlockCode = conditionalPenaltyBlockCodeForKind(
      ctx,
      'MEAL_BREAK',
      rawInput.conditionalPenaltyCode ?? null,
      employeeCategory,
    );
    const line = findConditionalPenaltyLine(ctx, 'MEAL_BREAK', employment, {
      blockCode: mealBreakBlockCode,
      employeeCategory,
    });
    if (line) {
      const trigger = line.triggerContinuousHours ?? 6;
      const continuousHours = rawInput.continuousHoursWithoutMealBreak ?? hoursWorked;
      penaltyPay = round2(
        penaltyPay +
          conditionalPenaltyExtraPay({
            baseHourlyRate: agedCareCasualWeekendUnloadedBase(resolved, employment, baseHourlyRate),
            hours: Math.max(0, continuousHours - trigger),
            totalMultiplier: line.multiplier,
            ordinaryMultiplier: conditionalOrdinaryMultiplier(employment),
          }),
      );
    } else {
      pushMissingConditionalPenaltyGap(
        coverageGaps,
        ctx,
        'MEAL_BREAK',
        employment,
        mealBreakBlockCode,
        employeeCategory,
      );
    }
  }

  if (rawInput.interruptedMealBreak) {
    const interruptedMealBreakBlockCode = conditionalPenaltyBlockCodeForKind(
      ctx,
      'INTERRUPTED_MEAL_BREAK',
      rawInput.conditionalPenaltyCode ?? null,
      employeeCategory,
    );
    const line = findConditionalPenaltyLine(ctx, 'INTERRUPTED_MEAL_BREAK', employment, {
      blockCode: interruptedMealBreakBlockCode,
      employeeCategory,
    });
    if (line) {
      penaltyPay = round2(
        penaltyPay +
          conditionalPenaltyExtraPay({
            baseHourlyRate: agedCareCasualWeekendUnloadedBase(resolved, employment, baseHourlyRate),
            hours: (rawInput.interruptedMealBreakMinutes ?? 30) / 60,
            totalMultiplier: line.multiplier,
            ordinaryMultiplier: conditionalOrdinaryMultiplier(employment),
          }),
      );
    } else {
      pushMissingConditionalPenaltyGap(
        coverageGaps,
        ctx,
        'INTERRUPTED_MEAL_BREAK',
        employment,
        interruptedMealBreakBlockCode,
        employeeCategory,
      );
    }
  }

  // Allowances (read from ctx)
  const allowanceLines: ShiftPayAllowanceLine[] = [];
  let allowancesPay = 0;

  function emit(code: string, label: string, amount: number, clauseRef: string | null = null) {
    if (amount <= 0) return;
    allowancesPay = round2(allowancesPay + amount);
    allowanceLines.push({ code, description: label, amount, clauseRef });
  }

  // Phase 1N (2026-05-17) — canonical emission codes per
  // `project_allowance_code_naming_taxonomy.md`. DB allowance_code keys
  // (SLEEPOVER / KM / UNIFORM_PER_SHIFT / LAUNDRY_PER_SHIFT) remain the
  // lookup keys against `ctx.allowances`; the emitted line `code` field
  // uses canonical _ALLOWANCE-suffixed names so the Xero batch-builder's
  // 1:1 map can route correctly without per-engine branching. Historical
  // pre-cutover allowance_lines_snapshot rows continue to use the old codes
  // (not rewritten — audit-correct policy).
  //
  // Phase 3 PR #3 W31 + W32 (2026-05-18) — FIRST_AID_ALLOWANCE and
  // BROKEN_SHIFT_ALLOWANCE emissions REMOVED. MA000018 has no first-aid
  // allowance row (cl 15.6 is the TOOL allowance for chefs/cooks at
  // $13.41/wk; the prior FIRST_AID emission was a Nurses MA000034 cl 18.3(b)
  // shape-port — 6th confirmed cross-award shape-porting instance). MA000018
  // has no broken-shift allowance — the prior BROKEN_SHIFT emission was a
  // SCHADS cl 20.12 shape-port. Both rolled back live + guarded by the W34
  // authorised-allowance-emissions registry. See
  // `docs/operations/clause-verifications/2026-05-18-aged-care-legislation-ma000018-cl-15-6-tiebreaker.md`.
  //
  // `rawInput.hasFirstAidResponsibility` and `rawInput.hasBrokenShift`
  // remain on the shared `ShiftPayInput` interface — SCHADS legitimately
  // consumes both. The AC engine simply does not read them.
  if (rawInput.hasSleepover) {
    const sleepover = resolveAllowance(ctx, 'SLEEPOVER', employment, coverageGaps);
    if (sleepover)
      emit(
        'SLEEPOVER_ALLOWANCE',
        sleepover.description ?? 'Sleepover',
        sleepover.amount,
        sleepover.clauseRef ?? null,
      );
  }
  if ((rawInput.travelKm ?? 0) > 0) {
    const km = resolveAllowance(ctx, 'KM', employment, coverageGaps);
    if (km)
      emit(
        'KM_ALLOWANCE',
        km.description ?? 'Travel km',
        round2((rawInput.travelKm ?? 0) * km.amount),
        km.clauseRef ?? null,
      );
  }
  if (rawInput.hasUniformAllowance) {
    const uniform = resolveAllowance(ctx, 'UNIFORM_PER_SHIFT', employment, coverageGaps);
    if (uniform)
      emit(
        'UNIFORM_ALLOWANCE',
        uniform.description ?? 'Uniform',
        uniform.amount,
        uniform.clauseRef ?? null,
      );
  }
  if (rawInput.hasLaundryAllowance) {
    const laundry = resolveAllowance(ctx, 'LAUNDRY_PER_SHIFT', employment, coverageGaps);
    if (laundry)
      emit(
        'LAUNDRY_ALLOWANCE',
        laundry.description ?? 'Laundry',
        laundry.amount,
        laundry.clauseRef ?? null,
      );
  }
  // Phase 3 PR #3 W31 (2026-05-18) — FIRST_AID_ALLOWANCE emission REMOVED.
  // MA000018 has no first-aid allowance row per FWC consolidated allowance
  // schedule MA000018 + FWO Pay Guide G01002985 (15 Jan 2026) — cl 15.6 is
  // the TOOL allowance for chefs/cooks. Prior emission was a Nurses MA000034
  // cl 18.3(b) shape-port (6th confirmed instance of the cross-award
  // shape-porting defect class). Tie-breaker artifact:
  // `docs/operations/clause-verifications/2026-05-18-aged-care-legislation-ma000018-cl-15-6-tiebreaker.md`.
  //
  // Phase 3 PR #3 W32 (2026-05-18) — BROKEN_SHIFT_ALLOWANCE emission REMOVED.
  // MA000018 has no broken-shift allowance row. Prior emission was a SCHADS
  // cl 20.12 shape-port (5th confirmed cross-award shape-porting instance).
  // The W34 authorised-allowance-emissions CI gate guards against
  // re-introduction.

  // Phase W4 (Award Engine Integrity 2026-05-17) — Nauseous work emission
  // per MA000018 cl 15.5(a). "Per hour or part thereof" rounds partial hours
  // UP via Math.ceil. NOT all-purpose — flat emission, casual loading does
  // NOT compound. See AGED_CARE_NAUSEOUS_WORK_EMISSION in award-data/aged-care.ts.
  // The resolved weekly minimum travels with the hourly evidence to weekly aggregation.
  if ((rawInput.nauseousWorkHours ?? 0) > 0) {
    const nauseous = resolveAllowance(
      ctx,
      AGED_CARE_NAUSEOUS_WORK_EMISSION.hourlyAllowanceCode,
      employment,
      coverageGaps,
    );
    if (nauseous) {
      const ceilHours = Math.ceil(rawInput.nauseousWorkHours ?? 0);
      // Phase 1N canonical emission code (DB lookup key remains 'NAUSEOUS_WORK').
      emit(
        'NAUSEOUS_WORK_ALLOWANCE',
        nauseous.description ?? 'Nauseous work',
        round2(nauseous.amount * ceilHours),
        nauseous.clauseRef ?? AGED_CARE_NAUSEOUS_WORK_EMISSION.clauseRef,
      );
      const minimum = resolveRequiredAllowance(
        ctx,
        AGED_CARE_NAUSEOUS_WORK_EMISSION.weeklyMinAllowanceCode,
        employment,
        { lookup: () => findWeeklyMinimumAllowance(ctx, employment, stream), coverageGaps },
      );
      if (minimum && allowanceLines.length > 0) {
        allowanceLines[allowanceLines.length - 1]!.weeklyMinimum = {
          amount: minimum.amount,
          scopeKey: weeklyMinimumScopeKey(ctx),
        };
      }
    }
  }

  // Phase 3 PR #3 sub-phase 3G (2026-05-18) — Annual leave loading per
  // MA000018 cl 28.4(a). Fires when segmentKind === 'paid_annual_leave' AND
  // employmentType is non-casual (NES s.86 — casuals receive 25% loading
  // on ordinary hours in lieu, not annual leave). Naive 17.5% × ordinary
  // leave pay (hoursWorked × baseHourlyRate, un-penalty-loaded — leave is
  // paid at ordinary time per cl 28.4(a) day-worker rule).
  //
  // Clause ref specialist-verified 2026-05-19; pre-fix cited cl 30.4
  // which is Personal/carer's leave. Clause renumbered 28.3 → 28.4 by
  // PR779150 ppc 01 Jan 2025.
  //
  // cl 28.4(b) shiftworker higher-of test is applied by the pay-run
  // leave-snapshot layer from rostered shift evidence. This engine branch
  // emits the flat cl 28.4(a) input for that comparison.
  if (rawInput.segmentKind === 'paid_annual_leave' && rawInput.employmentType !== 'casual') {
    const ordinaryLeavePay = round2(hoursWorked * baseHourlyRate);
    const loadingAmount = round2(ordinaryLeavePay * AGED_CARE_LEAVE.annualLeaveLoading);
    emit(
      'LEAVE_LOADING_ALLOWANCE',
      'Annual leave loading (17.5%)',
      loadingAmount,
      AGED_CARE_LEAVE.clauseRef,
    );
  }

  const grossTotal = sumMoney(basePay, penaltyPay, shiftLoadingPay, allowancesPay);

  const classificationPair = resolveActingVsSubstantive(
    stream,
    resolved.level,
    resolved.payPoint,
    rawInput.substantiveClassification,
  );

  const result: ShiftPayBreakdown = {
    normalizedLevel,
    baseHourlyRate,
    hoursWorked: round4(hoursWorked),
    bands: {
      weekdayOrdinary: round4(bands.weekdayOrdinary),
      saturdayOrdinary: round4(bands.saturdayOrdinary),
      sundayOrdinary: round4(bands.sundayOrdinary),
      publicHoliday: round4(bands.publicHoliday),
      eveningShift: round4(bands.eveningShift),
      nightShift: round4(bands.nightShift),
    },
    basePay,
    penaltyPay,
    shiftLoadingPay,
    overtimeHours: 0,
    overtimePay: 0,
    overtimeFirstHours: 0,
    overtimeRemainderHours: 0,
    overtimePublicHolidayHours: 0,
    allowancesPay,
    allowanceLines,
    grossTotal,
    warnings,
    ...coverageGapsField(coverageGaps),
    actingClassification: classificationPair.actingClassification,
    substantiveClassification: classificationPair.substantiveClassification,
    leadingHandAllPurposeLift,
  };
  rememberEnginePricingEvidence(result, {
    ordinaryAllocation: ctx.isEba ? 'ACTUAL_DAY' : 'WHOLE_SHIFT',
    segments: classified.segments.map((s) => ({
      start: s.startTime.toISOString(),
      end: s.endTime.toISOString(),
      day: s.dayType,
    })),
    ordinaryRates: {
      weekday: baseHourlyRate * weekdayMult,
      saturday: casualWeekendBase * satMult,
      sunday: casualWeekendBase * sunMult,
      publicHoliday: casualPublicHolidayBase * phMult,
    },
    loadingRates: {
      evening: ebaShiftLoadingLine
        ? agedCareCasualWeekendUnloadedBase(resolved, employment, baseHourlyRate) *
          (ebaShiftLoadingLine.multiplier - conditionalOrdinaryMultiplier(employment))
        : baseHourlyRate * commencementBandLoading * casualLoadingMult,
      night: ebaShiftLoadingLine
        ? agedCareCasualWeekendUnloadedBase(resolved, employment, baseHourlyRate) *
          (ebaShiftLoadingLine.multiplier - conditionalOrdinaryMultiplier(employment))
        : baseHourlyRate * commencementBandLoading * casualLoadingMult,
    },
    overtime: [],
  });
  ORDINARY_PAY_UNITS.set(result, {
    weekday: weekdayOrdinaryExact,
    saturday: saturdayExact,
    sunday: sundayExact,
    publicHoliday: publicHolidayExact,
  });
  return result;
}

function calculateWithOvertime(
  rawInput: ShiftPayWithOvertimeInput & AgedCareInputExtras,
  ctx?: RateContext,
  resolvedDailyLoading?: DailyPairShiftLoading,
): ShiftPayBreakdown {
  // Aged Care OT thresholds: 38h/week (cl 22 ordinary hours framework),
  // 10h/day (cl 22 + cl 25 OT trigger). Phase 1K citation correction
  // (was cl 21 = Superannuation).
  const dailyPair = ctx != null && hasGeneralStaffDailyLoadedOvertime(ctx);
  let baseBreakdown = calculate(rawInput, ctx);
  if (!ctx || baseBreakdown.baseHourlyRate === 0) return baseBreakdown;

  const totalShiftHours = baseBreakdown.hoursWorked;
  const ordinaryHoursForShift = resolveOrdinaryHoursForOvertime(rawInput, totalShiftHours, 10);
  const overtimeHours = Math.max(0, totalShiftHours - ordinaryHoursForShift);
  // Resolve and normalize before the ordinary-fragment return so settlement
  // boundaries cannot choose different catalogue or loading bases.
  const dailyLoading = dailyPair
    ? (resolvedDailyLoading ?? dailyPairShiftLoading(rawInput, ctx))
    : undefined;
  if (dailyLoading) baseBreakdown = calculate(rawInput, ctx, dailyLoading.ordinaryLoading);

  // Equivalent casual catalogues must expose the same unloaded basis for this
  // additive contract. Keep every weekend/holiday or mixed-day path held.
  if (
    rawInput.employmentType === 'casual' &&
    dailyPair &&
    (dailyLoading?.premium ?? 0) > 0 &&
    dailyLoading?.weekdayOnly &&
    baseBreakdown.bands.weekdayOrdinary > 0 &&
    baseBreakdown.bands.saturdayOrdinary === 0 &&
    baseBreakdown.bands.sundayOrdinary === 0 &&
    baseBreakdown.bands.publicHoliday === 0 &&
    ctx.rates.some((rate) => rate.includesCasualLoading === true)
  ) {
    return calculateWithOvertime(
      rawInput,
      {
        ...ctx,
        rates: ctx.rates.map((rate) =>
          rate.includesCasualLoading === true
            ? { ...rate, hourlyRate: rate.hourlyRate / 1.25, includesCasualLoading: false }
            : rate,
        ),
      },
      dailyLoading,
    );
  }

  if (overtimeHours <= 0) return baseBreakdown;

  // Phase 1B (Award Engine Integrity 2026-05) — per-day-type OT split using
  // getOvertimeSegmentBreakdown (Aged Care cl 25 OT + SCHADS-style inclusive
  // casual loading). Phase 1K citation correction (was cl 22 = Ordinary hours;
  // OT is at cl 25).
  // casual). See nurses.ts for the rationale and shift-classifier.ts §1.2.
  const tz = rawInput.timezone ?? 'Australia/Sydney';
  const isPublicHoliday = rawInput.isPublicHoliday ?? false;
  const baseHourlyRate = baseBreakdown.baseHourlyRate;
  const employment: EmploymentLabel = rawInput.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
  const coverageGaps: NonNullable<ShiftPayBreakdown['coverageGaps']> = [
    ...(baseBreakdown.coverageGaps ?? []),
  ];
  const shiftDate = dateOnlyInTz(new Date(rawInput.startTime), tz);
  const resolvedForOt = resolveAgedCareRate(ctx, rawInput, shiftDate, employment, coverageGaps);
  const casualMult = agedCareOrdinaryCasualFactor(resolvedForOt, employment);
  const penaltyLookup = (band: string, emp: EmploymentLabel) =>
    findPenalty(ctx.penalties, band, emp);
  const satMult = resolveRequiredPenalty(ctx, 'SATURDAY', employment, 1.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const sunMult = resolveRequiredPenalty(ctx, 'SUNDAY', employment, 2.0, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  const phMult = resolveRequiredPenalty(ctx, 'PUBLIC_HOLIDAY', employment, 2.5, {
    lookup: penaltyLookup,
    coverageGaps,
  });
  // Saturday overtime FLAT rate, cl 25.1(a)(i)(B). Deliberately the OT flat
  // rate and not satMult, which is the ORDINARY Saturday penalty (cl 23.2,
  // 175% casual and substitutional, not compounded).
  const satOtFlatMult = 2.0;

  const classificationTimes = shiftClassificationGeometry(rawInput);
  const classifiedForOT = classifyShift({
    startTime: classificationTimes.startTime,
    endTime: classificationTimes.endTime,
    breakMinutes: classificationTimes.breakMinutes,
    timezone: tz,
    isPublicHoliday,
    publicHolidaySegments: rawInput.publicHolidaySegments,
    shiftCharacterRules: AGED_CARE_SHIFT_RULES,
    dominantDayRule: 'STRICT_MAJORITY',
    permanentNightShiftWorker: false,
  });
  const otBreakdown = getOvertimeSegmentBreakdown(classifiedForOT, overtimeHours);
  const otByDay = otBreakdown.hoursByDayType;

  const overtimeRule = primaryOvertimeRuleFromContext(ctx);
  const weekdayRateIsPreloaded =
    dailyPair &&
    (dailyLoading?.premium ?? 0) > 0 &&
    employment === 'CASUAL' &&
    resolvedForOt.includesCasualLoading === true;
  const weekdayOvertimeBase = weekdayRateIsPreloaded ? baseHourlyRate / 1.25 : baseHourlyRate;
  const weekdayEmploymentMultiplier = weekdayRateIsPreloaded ? 1.25 : casualMult;
  const overtimeUsesLoadedRate = dailyPair
    ? (dailyLoading?.premium ?? 0) > 0
    : overtimeOnLoadedRateEnabledFromContext(ctx);
  const certEmployment = employment === 'CASUAL' ? 'CASUAL' : 'PERMANENT';
  const hasShiftLoadingOvertimeContext =
    (rawInput.conditionalPenaltyCode != null && rawInput.shiftStartTime != null) ||
    (baseBreakdown.shiftLoadingPay > 0 &&
      otByDay.weekday > 0 &&
      classifiedForOT.dominantDayType === 'weekday');
  let shiftLoadingPremiumForOvertime = 0;
  if (dailyPair) {
    shiftLoadingPremiumForOvertime = dailyLoading!.premium;
  } else if (overtimeUsesLoadedRate && hasShiftLoadingOvertimeContext) {
    const resolvedPremium = resolveShiftLoadingPremiumFromContext({
      ctx,
      employment: certEmployment,
      employeeCategory: rawInput.employeeCategory ?? null,
      shiftStartTime: rawInput.shiftStartTime ?? localTimeHHMM(rawInput.startTime, tz),
      conditionalPenaltyCode: rawInput.conditionalPenaltyCode ?? null,
      dayType: 'weekday',
    });
    if (resolvedPremium === null) {
      coverageGaps.push({
        band: rawInput.conditionalPenaltyCode ?? 'SHIFT_LOADING',
        employment,
        reason: EBA_REQUIRED_PENALTY_COVERAGE_GAP_REASON,
      });
    } else {
      shiftLoadingPremiumForOvertime = resolvedPremium;
    }
  }
  const weekdayTier1Mult = overtimeRule?.firstTwoHoursMultiplier ?? 1.5;
  const weekdayTier2Mult = overtimeRule?.afterTwoHoursMultiplier ?? 2.0;

  const selectedOvertime: EnginePricingEvidence['overtime'] = [];
  let overtimePay = 0;
  let tier1Remaining = Math.max(
    0,
    2 -
      Math.max(0, rawInput.overtimeInitialTierHoursConsumedEarlierThisShift ?? 0) -
      Math.max(
        0,
        dailyPair &&
          shiftLoadingPremiumForOvertime > 0 &&
          otByDay.weekday > 0 &&
          otByDay.saturday === 0 &&
          otByDay.sunday === 0 &&
          otByDay.publicHoliday === 0
          ? (rawInput.overtimeInitialTierHoursConsumedEarlierToday ?? 0)
          : 0,
      ),
  );

  // Carry the tier split out, not just the total. The component-line trigger
  // has to route these hours to a 150% or 200% Xero earnings rate, and with no
  // split reported it falls back to splitting by HOURS POSITION — which is
  // wrong for Sunday (200% from the first minute) and for public holidays
  // (250%). Measured on Medea pay run 69947580: 14 staff, $1,645.99 of 200%
  // overtime routed to the 150% rate.
  let otHoursAt15 = 0;
  let otHoursAt20 = 0;
  let otHoursAt25 = 0;

  if (otByDay.publicHoliday > 0) {
    overtimePay += otByDay.publicHoliday * baseHourlyRate * 2.5 * casualMult;
    selectedOvertime.push({
      band: 'otPublicHoliday',
      day: 'publicHoliday',
      hours: otByDay.publicHoliday,
      rate: baseHourlyRate * 2.5 * casualMult,
    });
    otHoursAt25 += otByDay.publicHoliday;
  }
  if (otByDay.sunday > 0) {
    overtimePay += otByDay.sunday * baseHourlyRate * 2.0 * casualMult;
    selectedOvertime.push({
      band: 'otRemainder',
      day: 'sunday',
      hours: otByDay.sunday,
      rate: baseHourlyRate * 2.0 * casualMult,
    });
    otHoursAt20 += otByDay.sunday;
  }
  // SATURDAY IS TRIGGER-DEPENDENT (cl 25.1, verified 2026-08-23 against FWC
  // consolidated MA000018 to 1 Jul 2026 — see
  // docs/operations/clause-verifications/2026-08-23-ma000018-saturday-overtime.md).
  //
  //   full-time, any trigger        cl 25.1(a)(i)(B)   Saturday FLAT 200%
  //   part-time weekly / rostered   cl 25.1(b)(i)(B)   Saturday FLAT 200%
  //                                 cl 25.1(b)(iii) redirects to (b)(i)
  //   part-time > 10 hours/day      cl 25.1(b)(ii)(A)  Saturday TIERED 150/200
  //   casual weekly                 cl 25.1(c)(i)(B)   Saturday FLAT 250%
  //   casual > 10 hours/day         cl 25.1(c)(ii)(A)  Saturday TIERED 187.5/250
  //
  // Banding ALL Saturday overtime with weekdays under-paid the first two hours
  // by 0.5x (20 items, 15 staff, $138.96 on Medea pay run 69947580). Making it
  // unconditionally flat would have over-paid the lawful > 10h/day case, so the
  // trigger has to be derived: any hour past the tenth in a day is the
  // > 10h/day trigger, everything else is weekly or excess-of-rostered.
  //
  // Full-time has only ONE trigger in the award — cl 25.1(a)(i) covers all of
  // its overtime — so a full-time Saturday is flat regardless.
  // Which Saturday overtime hours are the > 10h/day ones is a question about
  // POSITION IN THE SHIFT, not a count — and the first version of this got that
  // wrong.
  //
  // `Math.min(saturdayHours, totalShiftHours - 10)` assumed the hours past the
  // tenth are Saturday hours. `getOvertimeSegmentBreakdown` walks segments
  // BACKWARDS from the end, so on a shift that crosses into Sunday the past-ten
  // hours are SUNDAY hours, while Saturday's overtime sits at hours 8-10 —
  // excess of rostered hours, which cl 25.1(b)(iii) redirects to (b)(i) and
  // pays FLAT 200%.
  //
  // Part-time, $32.50, Saturday 14:00 to Sunday 02:00, 8h rostered:
  // overtime is the last 4h — Sat 22:00-24:00 and Sun 00:00-02:00. Correct is
  // 4 x 2.0 = $260.00. The count-based version tiered the two SATURDAY hours at
  // 150% and paid $227.50, underpaying $32.50 on one shift.
  //
  // Asking the same segment walker for just the last `overTenTriggerHours`
  // hours answers the positional question directly: whatever it reports as
  // Saturday genuinely lies past the tenth hour.
  const overTenTriggerHours = Math.max(0, totalShiftHours - 10);
  const saturdayHours = otByDay.saturday;
  const overTenSaturdayHours =
    overTenTriggerHours > 0
      ? getOvertimeSegmentBreakdown(classifiedForOT, Math.min(overtimeHours, overTenTriggerHours))
          .hoursByDayType.saturday
      : 0;
  const saturdayTieredHours =
    rawInput.isFullTime === true ? 0 : Math.min(saturdayHours, overTenSaturdayHours);
  const saturdayFlatHours = saturdayHours - saturdayTieredHours;

  if (saturdayFlatHours > 0) {
    overtimePay += saturdayFlatHours * baseHourlyRate * satOtFlatMult * casualMult;
    selectedOvertime.push({
      band: 'otRemainder',
      day: 'saturday',
      hours: saturdayFlatHours,
      rate: baseHourlyRate * satOtFlatMult * casualMult,
    });
    otHoursAt20 += saturdayFlatHours;
  }

  // Only the > 10h/day portion shares the tier-1 budget with weekday overtime.
  if (overtimeUsesLoadedRate && otByDay.weekday > 0) {
    const weekdayOtPay = calculateEbaTieredOvertimePay(
      weekdayOvertimeBase,
      otByDay.weekday,
      weekdayEmploymentMultiplier,
      {
        onLoadedRate: true,
        shiftLoadingIsAdditive: dailyPair,
        tier1HoursRemaining: dailyPair ? tier1Remaining : undefined,
        shiftLoadingPremium: shiftLoadingPremiumForOvertime,
        firstTwoHoursMultiplier: weekdayTier1Mult,
        afterTwoHoursMultiplier: weekdayTier2Mult,
      },
    );
    const selectedLoadedBase = dailyPair
      ? weekdayOvertimeBase * (weekdayEmploymentMultiplier + shiftLoadingPremiumForOvertime)
      : weekdayOvertimeBase * (1 + shiftLoadingPremiumForOvertime) * weekdayEmploymentMultiplier;
    const selectedFirstHours = Math.min(dailyPair ? tier1Remaining : 2, otByDay.weekday);
    selectedOvertime.push(
      {
        band: 'otFirst',
        day: 'weekday',
        hours: selectedFirstHours,
        rate: selectedLoadedBase * weekdayTier1Mult,
      },
      {
        band: 'otRemainder',
        day: 'weekday',
        hours: otByDay.weekday - selectedFirstHours,
        rate: selectedLoadedBase * weekdayTier2Mult,
      },
    );
    overtimePay += weekdayOtPay;
    const exportHours = otExportTierHours(
      otByDay.weekday,
      weekdayTier1Mult,
      weekdayTier2Mult,
      tier1Remaining,
    );
    otHoursAt15 += exportHours.otHoursAt15;
    otHoursAt20 += exportHours.otHoursAt20;
    tier1Remaining = Math.max(0, tier1Remaining - exportHours.otHoursAt15);
  }

  const tieredDays: Array<{ dt: DayType; hours: number }> = [
    { dt: 'saturday', hours: saturdayTieredHours },
    ...(overtimeUsesLoadedRate ? [] : [{ dt: 'weekday' as const, hours: otByDay.weekday }]),
  ];
  for (const { dt, hours: hoursInDay } of tieredDays) {
    if (hoursInDay <= 0) continue;
    const t1 = Math.min(tier1Remaining, hoursInDay);
    const t2 = hoursInDay - t1;
    selectedOvertime.push(
      { band: 'otFirst', day: dt, hours: t1, rate: baseHourlyRate * 1.5 * casualMult },
      { band: 'otRemainder', day: dt, hours: t2, rate: baseHourlyRate * 2.0 * casualMult },
    );
    overtimePay += (t1 * baseHourlyRate * 1.5 + t2 * baseHourlyRate * 2.0) * casualMult;
    otHoursAt15 += t1;
    otHoursAt20 += t2;
    tier1Remaining -= t1;
  }
  overtimePay = round2(overtimePay);

  // Substitution rule: per-day-type back-out of ordinary-rate dollars.
  const ordinaryOtByDay = ordinaryOvertimeBackoutHours(
    baseBreakdown.bands,
    otByDay,
    rawInput.publicHolidaySegments != null && !ctx.isEba,
  );
  let adjustedBasePay = baseBreakdown.basePay;
  let adjustedPenaltyPay = baseBreakdown.penaltyPay;
  // Per-unit back-out (RND-04/07): each band calculate() rounded on its own is
  // replaced by the once-rounded remainder; other money stays untouched.
  const ordinaryPayUnits = ORDINARY_PAY_UNITS.get(baseBreakdown);
  if (ordinaryOtByDay.weekday > 0) {
    adjustedBasePay =
      dailyPair && shiftLoadingPremiumForOvertime > 0
        ? round2(
            Math.max(0, baseBreakdown.bands.weekdayOrdinary - ordinaryOtByDay.weekday) *
              baseHourlyRate *
              casualMult,
          )
        : ordinaryPayUnits
          ? backOutRoundingUnits(adjustedBasePay, [
              {
                exact: ordinaryPayUnits.weekday,
                removal: ordinaryOtByDay.weekday * baseHourlyRate * casualMult,
              },
            ])
          : round2(
              Math.max(0, adjustedBasePay - ordinaryOtByDay.weekday * baseHourlyRate * casualMult),
            );
  }
  // cl 10.4(b) inclusive casual penalty bands — ordinary penalty pay uses the
  // resolved day multiplier only; do not compound casualMult on substitution.
  // GS EA cl 42(b): back out on the SAME unloaded base the ordinary Saturday/
  // Sunday pay was computed on (agedCareCasualWeekendUnloadedBase), not the
  // raw (possibly pre-loaded) baseHourlyRate — otherwise the back-out would
  // remove too much or too little relative to what calculate() actually paid.
  const casualWeekendBaseForOt = agedCareCasualWeekendUnloadedBase(
    resolvedForOt,
    employment,
    baseHourlyRate,
  );
  const directPenaltyBackout =
    ordinaryPayUnits &&
    (ordinaryOtByDay.saturday > 0 ||
      ordinaryOtByDay.sunday > 0 ||
      ordinaryOtByDay.publicHoliday > 0)
      ? backOutRoundingUnits(adjustedPenaltyPay, [
          {
            exact: ordinaryPayUnits.saturday,
            removal: ordinaryOtByDay.saturday * casualWeekendBaseForOt * satMult,
          },
          {
            exact: ordinaryPayUnits.sunday,
            removal: ordinaryOtByDay.sunday * casualWeekendBaseForOt * sunMult,
          },
          {
            exact: ordinaryPayUnits.publicHoliday,
            removal:
              ordinaryOtByDay.publicHoliday *
              agedCareCasualWeekendUnloadedBase(resolvedForOt, employment, baseHourlyRate) *
              phMult,
          },
        ])
      : null;
  if (directPenaltyBackout !== null) adjustedPenaltyPay = directPenaltyBackout;
  if (directPenaltyBackout === null && ordinaryOtByDay.saturday > 0) {
    adjustedPenaltyPay = round2(
      Math.max(0, adjustedPenaltyPay - ordinaryOtByDay.saturday * casualWeekendBaseForOt * satMult),
    );
  }
  if (directPenaltyBackout === null && ordinaryOtByDay.sunday > 0) {
    adjustedPenaltyPay = round2(
      Math.max(0, adjustedPenaltyPay - ordinaryOtByDay.sunday * casualWeekendBaseForOt * sunMult),
    );
  }
  if (directPenaltyBackout === null && ordinaryOtByDay.publicHoliday > 0) {
    adjustedPenaltyPay = round2(
      Math.max(
        0,
        adjustedPenaltyPay -
          ordinaryOtByDay.publicHoliday *
            agedCareCasualWeekendUnloadedBase(resolvedForOt, employment, baseHourlyRate) *
            phMult,
      ),
    );
  }

  let adjustedShiftLoadingPay = baseBreakdown.shiftLoadingPay;
  const minorityWeekdayLoading =
    !dailyPair && ctx.isEba && classifiedForOT.dominantDayType !== 'weekday'
      ? resolveShiftLoading(ctx, employment, {
          employeeCategory: rawInput.employeeCategory ?? null,
          shiftStartTime: localTimeHHMM(classificationTimes.startTime, tz),
          shiftEndTime: localTimeHHMM(classificationTimes.endTime, tz),
        })
      : undefined;
  const ordinaryShiftLoadingPremium = dailyPair
    ? dailyLoading!.ordinaryLoading.premium
    : (minorityWeekdayLoading?.premium ?? shiftLoadingPremiumForOvertime);
  if (
    (overtimeUsesLoadedRate ||
      ((dailyPair || minorityWeekdayLoading) && ordinaryShiftLoadingPremium > 0)) &&
    otByDay.weekday > 0 &&
    ordinaryShiftLoadingPremium > 0 &&
    adjustedShiftLoadingPay > 0
  ) {
    adjustedShiftLoadingPay =
      dailyPair || minorityWeekdayLoading
        ? round2(
            Math.max(
              0,
              baseBreakdown.bands.eveningShift + baseBreakdown.bands.nightShift - otByDay.weekday,
            ) *
              agedCareCasualWeekendUnloadedBase(resolvedForOt, employment, baseHourlyRate) *
              ordinaryShiftLoadingPremium,
          )
        : round2(
            Math.max(
              0,
              adjustedShiftLoadingPay -
                otByDay.weekday * baseHourlyRate * shiftLoadingPremiumForOvertime * casualMult,
            ),
          );
  }

  // Phase W4 (Award Engine Integrity 2026-05-17) — Meal-OT emission per
  // MA000018 cl 15.4(a)(i) + cl 15.4(a)(ii). First meal $16.62 when OT > 1h
  // past usual finish; second meal $14.98 additional when OT > 4h cumulative.
  // CUMULATIVE semantics (worker doing 5h OT gets $16.62 + $14.98). NOT
  // all-purpose — flat emission, casual loading does NOT compound. Carve-outs:
  // provision-of-meal (cl 15.4(a)(i)) suppresses both; rare could-return-
  // home-for-meal also suppresses. See AGED_CARE_MEAL_OT_EMISSION in
  // award-data/aged-care.ts.
  const allowanceLinesOut: ShiftPayAllowanceLine[] = [...baseBreakdown.allowanceLines];
  let allowancesPayOut = baseBreakdown.allowancesPay;
  const mealCarveOut =
    (rawInput.mealProvidedDuringOT ?? false) || (rawInput.couldReturnHomeForMeal ?? false);
  const mealCrossings = overtimeMealAllowanceCrossings({
    fragmentOvertimeHours: overtimeHours,
    priorOvertimeHoursThisShift: rawInput.overtimeHoursWorkedEarlierThisShift,
    firstMealThresholdHours: AGED_CARE_MEAL_OT_EMISSION.firstMealThresholdHours,
    secondMealThresholdHours: AGED_CARE_MEAL_OT_EMISSION.secondMealThresholdHours,
  });
  if (mealCrossings.emitFirstMeal && !mealCarveOut) {
    const firstMeal = resolveAllowance(
      ctx,
      AGED_CARE_MEAL_OT_EMISSION.firstMealAllowanceCode,
      employment,
      coverageGaps,
    );
    if (firstMeal) {
      allowancesPayOut = round2(allowancesPayOut + firstMeal.amount);
      // Phase 1N canonical emission code (DB lookup key remains 'MEAL').
      allowanceLinesOut.push({
        code: 'MEAL_ALLOWANCE',
        description: firstMeal.description ?? 'Meal allowance (overtime)',
        amount: firstMeal.amount,
        clauseRef: firstMeal.clauseRef ?? AGED_CARE_MEAL_OT_EMISSION.clauseRefFirst,
      });
    }
    if (mealCrossings.emitSecondMeal) {
      const secondMeal = resolveAllowance(
        ctx,
        AGED_CARE_MEAL_OT_EMISSION.secondMealAllowanceCode,
        employment,
        coverageGaps,
      );
      if (secondMeal) {
        allowancesPayOut = round2(allowancesPayOut + secondMeal.amount);
        // Phase 1N canonical emission code (DB lookup key remains 'MEAL_OT_ADDITIONAL').
        allowanceLinesOut.push({
          code: 'MEAL_OT_ADDITIONAL_ALLOWANCE',
          description: secondMeal.description ?? 'Additional meal allowance (further OT > 4h)',
          amount: secondMeal.amount,
          clauseRef: secondMeal.clauseRef ?? AGED_CARE_MEAL_OT_EMISSION.clauseRefSecond,
        });
      }
    }
  }

  const grossTotal = sumMoney(
    adjustedBasePay,
    adjustedPenaltyPay,
    adjustedShiftLoadingPay,
    overtimePay,
    allowancesPayOut,
  );

  const result: ShiftPayBreakdown = {
    ...baseBreakdown,
    bands: subtractOvertimeFromOrdinaryBands(baseBreakdown.bands, ordinaryOtByDay),
    basePay: adjustedBasePay,
    penaltyPay: adjustedPenaltyPay,
    shiftLoadingPay: adjustedShiftLoadingPay,
    overtimeHours: round4(overtimeHours),
    overtimeFirstHours: round4(otHoursAt15),
    overtimeRemainderHours: round4(otHoursAt20),
    overtimePublicHolidayHours: round4(otHoursAt25),
    overtimePay,
    allowancesPay: allowancesPayOut,
    allowanceLines: allowanceLinesOut,
    grossTotal,
    ...coverageGapsField(coverageGaps),
  };
  const ordinaryEvidence = enginePricingEvidence(baseBreakdown);
  return ordinaryEvidence
    ? rememberEnginePricingEvidence(result, { ...ordinaryEvidence, overtime: selectedOvertime })
    : result;
}

/**
 * Semver of the Aged Care Award (MA000018) engine. Bumped manually on every
 * math-affecting change to this file. Persisted to
 * `pay_run_items.snapshot_engine_version` via `finalise_pay_run_snapshot`,
 * so each row is byte-reproducible against the engine code that produced it.
 *
 * Enforced by `.husky/pre-commit` + CI assertion: any modification to this
 * file (other than the version line itself) requires this string to change,
 * unless the commit message contains `[engine-no-math-change]`.
 */
// Phase 1N (2026-05-17) — bumped 5.5.8 → 5.6.0 (semver MINOR). Output contract
// change: allowance_lines_snapshot[*].code values now use canonical
// _ALLOWANCE-suffixed names per `project_allowance_code_naming_taxonomy.md`.
// SLEEPOVER → SLEEPOVER_ALLOWANCE; KM → KM_ALLOWANCE; UNIFORM_PER_SHIFT →
// UNIFORM_ALLOWANCE; LAUNDRY_PER_SHIFT → LAUNDRY_ALLOWANCE; FIRST_AID →
// FIRST_AID_ALLOWANCE; BROKEN_SHIFT → BROKEN_SHIFT_ALLOWANCE; NAUSEOUS_WORK →
// NAUSEOUS_WORK_ALLOWANCE; MEAL → MEAL_ALLOWANCE; MEAL_OT_ADDITIONAL →
// MEAL_OT_ADDITIONAL_ALLOWANCE. Engine math UNCHANGED; only emission code
// strings renamed. Pre-cutover historical pay_run_items rows preserved
// (audit-correct policy per CLAUDE.md migration discipline). Bidirectional
// handler at `packages/utils/src/allowance-code-canonical.ts` translates
// historical codes for reads.
//
// Phase 3 PR #3 sub-phase 3G (2026-05-18) — bumped 5.6.0 → 5.6.1 (semver PATCH).
// New LEAVE_LOADING_ALLOWANCE emission per cl 28.4(a) gated on
// segmentKind === 'paid_annual_leave' AND non-casual employmentType. Flat
// 17.5% × ordinary leave pay; cl 28.4(b) shiftworker higher-of is applied by
// the pay-run leave-snapshot layer. (Clause specialist-verified 2026-05-19; pre-fix cited
// cl 30.4 which is Personal/carer's leave; renumbered 28.3 → 28.4 by
// PR779150 ppc 01 Jan 2025.)
// All pre-3G callers default segmentKind='work' → byte-equal for work
// segments. T2 sweep + Path-A pins cover the new emission path.
//
// Phase Codex Pass A (2026-05-19) — bumped 5.6.1 → 5.7.0 (semver MINOR).
// MA000018 cl 26.1 commencement-band classification REBUILT per
// primary-source verification. Pre-Phase finish-only Nurses-shape port
// mis-classified afternoon (1pm-4pm-commence) as 10% (correct 12.5%),
// night (4pm-4am-commence) as 10% afternoon (correct 15%), and missed
// the 4am-6am early-morning 10% band entirely. Engine math CHANGED for
// affected shift patterns; byte-equal preserved for shifts that
// commence between 06:00 and 10:00 (no shift loading applies on either
// shape) and for non-shiftworker employee category. New
// `classifyAgedCareCommencementBand` helper + `AGED_CARE_COMMENCEMENT_BAND_LOADINGS`
// hardcoded loading lookup (DB seed only covers 2 of 4 bands; AWR 2026
// reseed 22 Jun 2026 MUST re-verify). FW Act s.327A wage-theft criminal
// regime exposure resolved (direction: under-pay → corrected up).
// Artifact: docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-shift-classification-primary-source-rebuild.md
//
// Phase Codex Pass A PR #B (2026-05-19) — bumped 5.7.0 → 5.7.1 (semver PATCH).
// [engine-math-changed] tag — `calculateRecall` math materially changed
// per FWC cl 25.1(a) primary-source verification. Pre-fix
// `AgedCareEngine.calculateRecall` collapsed all paid hours to a single
// flat 1.5× tier-1 multiplier; post-fix applies banded OT for Mon-Fri
// (first 2h @ 150% / casual 187.5%, after 2h @ 200% / casual 250% per
// cl 25.1(a)(i)(A)), FLAT 200% / casual 250% for Saturday (cl
// 25.1(a)(i)(B); NEW finding — NOT banded, distinct from Nurses /
// HPSS / SCHADS Mon-Sat tiered shape), FLAT 200% Sunday, FLAT 250% PH
// (cl 25.1(a)(ii)). Casual loading is MULTIPLICATIVE per cl 25.1 NOTE.
// `calculate` + `calculateWithOvertime` (timesheet path) are UNCHANGED —
// only recall pay is affected. Artifact:
// docs/operations/clause-verifications/2026-05-19-statura-regulatory-source-recall-banded-ot.md
//
// EA ordinary-hours cycle bridge (2026-06-09) — bumped 5.7.1 → 5.7.2
// (semver PATCH). `calculateWithOvertime` now accepts configured ordinary
// cycle caps and returns ordinary-hour bands with OT-tail hours removed, so
// pay-run hour columns and cumulative ordinary tracking match OT substitution.
// Legacy callers without cycle inputs remain byte-equal for pay dollars.
//
// EBA Phase 2 strict-mode (2026-07-03) — bumped 5.7.3 → 5.7.6. EBA
// required Sat/Sun/PH penalty bands now fail loud with coverageGaps instead
// of falling back to modern-award constants. Aged Care afternoon/night EBA
// ctx-read is deliberately deferred to the cl 44 span rebuild because the
// current shared WEEKDAY_AFTERNOON/WEEKDAY_NIGHT bands conflate distinct
// commencement loadings. `calculateWithOvertime` also backs out OT-substituted
// Sat/Sun/PH ordinary dollars using resolved EBA penalty multipliers. Non-EBA
// output is byte-equal by the Phase 2 golden.
//
// EBA Phase 3 follow-up (2026-07-04) — bumped 5.7.6 → 5.7.7. Required EBA
// allowance rows now fail loud through coverageGaps when a triggered Aged Care
// allowance lookup is missing. Non-EBA output is byte-equal.
//
// EBA Phase 3 follow-up (2026-07-04) — bumped 5.7.7 → 5.7.8. Recall and
// sleepover disturbance penalty multipliers now fail loud through coverageGaps
// when a required EBA penalty row is missing.
//
// Medea payroll readiness (2026-07-06) — bumped 5.7.10 → 5.7.11. Shared
// OT band substitution now removes weekday loading overlays and ordinary-hour
// replay no longer double-counts loading overlays.
//
// Medea payroll readiness (2026-07-07) — bumped 5.7.11 → 5.7.12.
// Provider-managed EBA contexts now allocate ordinary weekday/weekend/PH bands
// from worked day-boundary segments, using ordered break-treatment segments
// when present. Base-award MA000018 keeps whole-shift majority attribution.
//
// Medea payroll readiness (2026-07-07) — bumped 5.7.12 → 5.7.13.
// EBA SHIFT_START_WINDOW penalties now require an explicit source block code;
// ordinary rows that only start inside a configured window stay ordinary pay.
//
// Medea payroll readiness (2026-07-08) — bumped 5.7.13 → 5.7.14.
// Aged Care EBA SHIFT_START_WINDOW loadings are now suppressed when the
// whole-shift classifier is weekend/PH dominant, while ordinary weekend/weekday
// dollars remain day-boundary segmented per 5.7.12.
// E4 per-EA shift loading (2026-07-15) — bumped 5.7.15 → 5.7.16. New
// category-scoped path: General EA cl 44 span for shift-workers / cl 6
// commencement for day-workers, resolved from shift times + employee_category,
// fail-closed on unknown category. Output is byte-identical for legacy
// (non-category-scoped) data; the new mechanic activates only with a
// category-scoped rule version.
// E4 review round (2026-07-15) — bumped 5.7.16 → 5.7.17. Shared category
// resolver: an UNRECOGNISED category (OTHER / typo) now fails closed, not paid
// as a day-worker; casual conditional multiplier no longer pre-rounded.
// Casual shift-loading correction (2026-07-25) — bumped 5.7.17 → 5.7.18. Two
// changes ship under this version:
//  1. EBA SHIFT_START_WINDOW loadings for casuals are now ADDITIVE with the
//     casual loading (cl 44 / cl 6 "of the ordinary hourly rate") instead of
//     compounding on the casual-loaded rate. Affects INCLUSIVE-casual profiles
//     (MA000018 EAs) only; Nurses MA000034 BASE_PLUS_ENGINE_LOADING unchanged.
//  2. A conditional line whose block explicitly DECLARES a casual rate is now
//     taken at face value rather than re-derived — no current tenant is
//     affected; it closes a provider-onboarding double-count.
// Medea missed-meal-break cause gate (2026-07-30) — bumped 5.7.18 → 5.7.19.
// Approved cause evidence can now route explicit-zero MA000018 events into the
// existing post-six-worked-hours MEAL_BREAK premium shape.
// P45 (2026-09-16) — honour `includesCasualLoading` on catalogue hourly rates
// so loaded casual GS rows (Marni Kaulima / M7) are not multiplied again.
// Release merge (2026-09-17) — 5.7.27 (wave 4) + main 5.7.26 → 5.7.28.
// GS EA cl 42(b) re-derivation (2026-09-17) — 5.7.28 → 5.7.29. Casual
// Saturday/Sunday ORDINARY hours now price at 175%/225% of the UNLOADED
// ordinary hourly rate (dividing a pre-loaded catalogue row back out, not
// merely neutralising the casual factor to 1.0), superseding the 41.9585
// P45 target with the clause-correct 58.7419. See
// docs/operations/clause-verifications/2026-09-17-aged-care-legislation-medea-gs-casual-weekend-penalty-substitution-fix-shape.md.
// Persisted GS daily pair: additive loaded-rate weekday OT, with tier carry and ordinary-only loading.
// Casual loaded-rate parity: public-holiday ordinary/OT substitution and
// conditional penalties use the unloaded base; unflagged money is unchanged.
// MZ-004 D5 (2026-10-05) — 5.7.35 → 5.7.38 (5.7.36 is claimed by the TZ
// remediation branch, 5.7.37 by the disturbance PR #3419). A permanent work
// segment that crosses a DST-start gap on its classifying clock gains a
// separate DST_START_ORDINARY_TOP_UP_ALLOWANCE line for the skipped wall-clock
// time at the ordinary rate. Every other shift is byte-equal. See
// docs/operations/clause-verifications/2026-10-05-mz004-multi-timezone-pay-semantics.md.
// MZ-004 S3 (2026-10-06) — 5.7.38 → 5.7.39. MA000018 cl 29.2(a) full-time
// public-holiday election: ANNUAL_LEAVE_HOURS pays ordinary time only on the
// base award and records the hours for the leave ledger. No election = byte-equal.
// MZ-004 R3 (2026-10-06) — 5.7.39 → 5.7.40. cl 29.2(b)(ii): part-time
// employees have the same choice; outcome clauseRef names (a)(ii) or (b)(ii).
// Callers pass the election only when the leave credit is postable.
// TZ-008 (2026-10-07) — 5.7.40 → 5.7.41. The age-band shift date
// is the WORK-location local date (dateOnlyInTz with the work timezone callers
// pass, consistent with MZ-004 D1's work-location clock), not a UTC slice, so a
// local eighteenth birthday morning prices at the adult rate. See TZ-008.
// S0-05 integration — 5.7.41 → 5.7.42: both-clock calendar spans feed
// the shared classifier, retaining TZ-008's age-band work date.
// P54 coherence (2026-10-07) — 5.7.42 → 5.7.43: with public-holiday segments,
// minute-granular break-record day-type hours are scaled onto the clock-span
// paid hours when they differ only by sub-minute seconds, so ordinary +
// overtime equals hours worked and recompute/snapshot match preflight. No
// clause, rate or multiplier change.
// Half-cent rounding (2026-10-08) — 5.7.43 → 5.7.44. round2 now uses the shared
// exact half-up roundMoney (money-rounding.ts); exact half-cent amounts round
// up instead of down. No clause, rate or multiplier change.
// Overtime back-out rounds once (2026-10-08, Astra RND-04) — 5.7.44 → 5.7.45. The
// remaining ordinary/penalty dollars of hours that became overtime are priced
// once per engine rounding unit (backOutRoundingUnits) instead of
// round(round(total) − back-out), which drifted ±1c. No clause, rate or
// multiplier change.
// Overtime back-out per rounding unit (2026-10-08, Astra RND-07) — 5.7.45 → 5.7.46.
// calculate() records its exact per-band ordinary pay units; the back-out
// replaces only the units that lose hours (backOutRoundingUnits), so other
// separately rounded money in the component (e.g. a missed-meal penalty) is
// never re-rounded away. No clause, rate or multiplier change.
// CAP Round 10: retain exact selected pricing evidence for frozen ledger replay.
export const ENGINE_VERSION = '5.7.52';

/**
 * MA000018 cl 29.2(a): a full-time employee working a public holiday receives
 * ordinary pay and elects either an additional 150% (CASH_150, default) or the
 * same number of hours added to annual leave (ANNUAL_LEAVE_HOURS). For the
 * latter, the public-holiday ordinary hours keep 100% only and the breakdown
 * records the hours for the leave ledger. Overtime on a public holiday is a
 * separate clause (25.1(a)(i)(C)) and is untouched. Only the base award: an
 * enterprise agreement's own public-holiday terms govern under an agreement.
 * Clause text: docs/regulatory-sources/fwc-modern-awards-2026-10-05/MA000018.txt
 * lines 1172-1178.
 */
function applyPublicHolidayElection(
  breakdown: ShiftPayBreakdown,
  input: ShiftPayInput & AgedCareInputExtras,
  ctx: RateContext | undefined,
): ShiftPayBreakdown {
  if (input.publicHolidayElection !== 'ANNUAL_LEAVE_HOURS') return breakdown;
  if (!(breakdown.bands.publicHoliday > 0)) return breakdown;
  if (!ctx || ctx.isEba) {
    return {
      ...breakdown,
      warnings: [
        ...breakdown.warnings,
        'The worker chose to add public holiday hours to annual leave, but an enterprise agreement applies, so its public holiday terms were paid instead.',
      ],
    };
  }
  // cl 29.2(a)(ii) full-time and cl 29.2(b)(ii) part-time; never casual (cl 29.2(c)).
  if (input.employmentType === 'casual') return breakdown;
  if ((input.segmentKind ?? 'work') !== 'work') return breakdown;
  if (!(breakdown.baseHourlyRate > 0)) return breakdown;
  const phMult = findPenalty(ctx.penalties, 'PUBLIC_HOLIDAY', 'PERMANENT') ?? 2.5;
  if (!(phMult > 1)) return breakdown;
  const hours = breakdown.bands.publicHoliday;

  const premium = round2(hours * breakdown.baseHourlyRate * (phMult - 1));
  const outcome: AgedCarePublicHolidayElectionOutcome = {
    election: 'ANNUAL_LEAVE_HOURS',
    leaveCreditHours: Math.round(hours * 10_000) / 10_000,
    cashPremiumNotPaid: premium,
    clauseRef:
      input.isFullTime === true
        ? AGED_CARE_PUBLIC_HOLIDAY_ELECTION_CLAUSE
        : AGED_CARE_PUBLIC_HOLIDAY_ELECTION_CLAUSE_PART_TIME,
  };
  return {
    ...breakdown,

    penaltyPay: round2(breakdown.penaltyPay - premium),

    grossTotal: sumMoney(breakdown.grossTotal, -premium),
    warnings: [
      ...breakdown.warnings,
      `Public holiday hours (${outcome.leaveCreditHours}) are added to annual leave instead of the extra 150% payment, as the worker chose (cl 29.2(${input.isFullTime === true ? 'a' : 'b'})).`,
    ],
    publicHolidayElection: outcome,
  } as AgedCareShiftPayBreakdown;
}

export class AgedCareEngine implements AwardEngine {
  readonly awardCode: AwardCode = 'MA000018';
  readonly engineVersion: string = ENGINE_VERSION;
  readonly engineRevisionSha: string = process.env.STATURA_ENGINE_REVISION ?? 'dev';
  buildEvaluationTrace(
    input: ShiftPayInput | ShiftPayWithOvertimeInput,
    ctx?: RateContext,
  ): EvaluationTrace {
    if (!ctx) return unavailableEvaluationTrace('AGED_CARE_RATE_CONTEXT_REQUIRED');
    const typed = input as ShiftPayWithOvertimeInput & AgedCareInputExtras;
    const breakdown =
      'ordinaryHoursLimitPerShift' in input
        ? calculateWithOvertime(typed, ctx)
        : calculate(typed, ctx);
    if (breakdown.baseHourlyRate <= 0 || typed.hasSleepover) {
      return unavailableEvaluationTrace(
        typed.hasSleepover
          ? 'AGED_CARE_SLEEPOVER_INTERVALS_NOT_CLASSIFIED'
          : 'AGED_CARE_RATE_UNRECOGNISED',
      );
    }
    const timezone = typed.timezone;
    if (!timezone) return unavailableEvaluationTrace('AGED_CARE_TENANT_TIMEZONE_REQUIRED');
    const geometry = shiftClassificationGeometry(typed);
    if (
      geometry.startTime.getTime() !== input.startTime.getTime() ||
      geometry.endTime.getTime() !== input.endTime.getTime()
    ) {
      return unavailableEvaluationTrace('AGED_CARE_FRAGMENT_ENVELOPE_CANNOT_FORM_ITEM_TRACE');
    }
    const employment: EmploymentLabel = typed.employmentType === 'casual' ? 'CASUAL' : 'PERMANENT';
    const base = breakdown.baseHourlyRate;
    const regularBand =
      breakdown.bands.publicHoliday > 0
        ? 'PUBLIC_HOLIDAY'
        : breakdown.bands.sundayOrdinary > 0
          ? 'SUNDAY'
          : breakdown.bands.saturdayOrdinary > 0
            ? 'SATURDAY'
            : 'WEEKDAY_DAY';
    const regularMultiplier =
      regularBand === 'PUBLIC_HOLIDAY'
        ? (findPenalty(ctx.penalties, 'PUBLIC_HOLIDAY', employment) ?? 2.5)
        : regularBand === 'SUNDAY'
          ? (findPenalty(ctx.penalties, 'SUNDAY', employment) ?? 2)
          : regularBand === 'SATURDAY'
            ? (findPenalty(ctx.penalties, 'SATURDAY', employment) ?? 1.5)
            : agedCareOrdinaryCasualFactor({ includesCasualLoading: undefined }, employment);
    // Aged Care classifies base-award ordinary time as a whole shift. The
    // selected dominant-day and commencement band above are therefore used for
    // every classifier cell, exactly as calculate() allocates its `bands`.
    const commencementBand = classifyAgedCareCommencementBand(geometry.startTime, timezone);
    const loading =
      breakdown.bands.eveningShift > 0 || breakdown.bands.nightShift > 0 ? commencementBand : null;
    if (breakdown.overtimeHours > 0 && loading)
      return unavailableEvaluationTrace('AGED_CARE_LOADED_OVERTIME_PER_INTERVAL_RATE_NOT_RETAINED');
    const loadingExtra = loading ? AGED_CARE_COMMENCEMENT_BAND_LOADINGS[loading] : 0;
    const totalPaidMs = Math.round(breakdown.hoursWorked * 3_600_000);
    const otStartMs = totalPaidMs - Math.round(breakdown.overtimeHours * 3_600_000);
    const price = (
      at: Date,
      allocation: 'ORDINARY' | 'OVERTIME',
      paidBefore: number,
    ): TracePrice => {
      let multiplier = regularMultiplier;
      let band = loading ? `AGED_CARE_${loading}` : regularBand;
      let component = loading ? 'ORDINARY_PLUS_SHIFT_LOADING' : 'ORDINARY';
      if (allocation === 'OVERTIME') {
        const otOrdinal = paidBefore - otStartMs;
        const actualDay = typed.publicHolidaySegments
          ? classified.segments.find((cell) => at >= cell.startTime && at < cell.endTime)?.dayType
          : undefined;
        const overtimeBand =
          actualDay === undefined
            ? regularBand
            : actualDay === 'publicHoliday'
              ? 'PUBLIC_HOLIDAY'
              : actualDay === 'sunday'
                ? 'SUNDAY'
                : actualDay === 'saturday'
                  ? 'SATURDAY'
                  : 'WEEKDAY_DAY';
        if (overtimeBand === 'PUBLIC_HOLIDAY') multiplier = 2.5;
        else if (overtimeBand === 'SUNDAY') multiplier = 2;
        else if (overtimeBand === 'SATURDAY') multiplier = 2;
        else multiplier = otOrdinal < 2 * 3_600_000 ? 1.5 : 2;
        multiplier *= employment === 'CASUAL' ? 1.25 : 1;
        band =
          overtimeBand === 'WEEKDAY_DAY'
            ? otOrdinal < 2 * 3_600_000
              ? 'OVERTIME_TIER_1'
              : 'OVERTIME_TIER_2'
            : `${overtimeBand}_OVERTIME`;
        component = 'OVERTIME';
      } else if (loading)
        multiplier +=
          loadingExtra *
          agedCareOrdinaryCasualFactor({ includesCasualLoading: undefined }, employment);
      const rateCents6 = Math.round(base * multiplier * 100 * 1_000_000);
      // The engine rounds weekday base, the (single) weekend/PH penalty band,
      // the shift loading and overtime as separate components (Astra RND-02):
      // the trace reconciles on those same groups.
      const regularGroup = regularBand === 'WEEKDAY_DAY' ? 'BASE' : 'PENALTY';
      const regularCents6 = Math.round(base * regularMultiplier * 100 * 1_000_000);
      const components =
        component === 'OVERTIME'
          ? [{ group: 'OVERTIME', rateCents6 }]
          : component === 'ORDINARY_PLUS_SHIFT_LOADING' && rateCents6 !== regularCents6
            ? [
                { group: regularGroup, rateCents6: regularCents6 },
                { group: 'SHIFT_LOADING', rateCents6: rateCents6 - regularCents6 },
              ]
            : [{ group: regularGroup, rateCents6 }];
      return {
        band,
        component,
        multiplierBasisPoints: Math.round(multiplier * 10_000),
        rateCents6,
        components,
      };
    };
    const classified = classifyShift({
      startTime: geometry.startTime,
      endTime: geometry.endTime,
      breakMinutes: 0,
      timezone,
      isPublicHoliday: typed.isPublicHoliday ?? false,
      publicHolidaySegments: typed.publicHolidaySegments,
      shiftCharacterRules: AGED_CARE_SHIFT_RULES,
      dominantDayRule: 'STRICT_MAJORITY',
    });
    return buildNativeEvaluationTrace({
      input,
      breakdown,
      segments: classified.segments,
      overtimeMilliseconds: Math.round(breakdown.overtimeHours * 3_600_000),
      price,
    });
  }

  calculate(input: ShiftPayInput, ctx?: RateContext): ShiftPayBreakdown {
    const typed = input as ShiftPayInput & AgedCareInputExtras;
    return applyDstStartOrdinaryTopUp(
      applyPublicHolidayElection(calculate(typed, ctx), typed, ctx),
      input,
      this.awardCode,
    );
  }
  calculateWithOvertime(input: ShiftPayWithOvertimeInput, ctx?: RateContext): ShiftPayBreakdown {
    const typed = input as ShiftPayWithOvertimeInput & AgedCareInputExtras;
    return applyDstStartOrdinaryTopUp(
      applyPublicHolidayElection(calculateWithOvertime(typed, ctx), typed, ctx),
      input,
      this.awardCode,
    );
  }

  calculateRecall(
    input: RecallPayInput,
    ctx: RateContext,
    baseHourlyRate: number,
    employmentType: 'PERMANENT' | 'CASUAL',
  ): RecallPayBreakdown {
    return calculateRecallPay(input, AGED_CARE_RECALL_RULES, ctx, baseHourlyRate, employmentType);
  }
}

export const agedCareEngine = new AgedCareEngine();
